// src/pages/CaseAnalyzerStatus.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getCaseAnalyzerStatus,
  getCaseAnalyzerHistory,
  getCaseAnalyzerDashboard,
} from "../utils/caseAnalyzerApiClient";
import AnswerRenderer from "../components/answer/AnswerRenderer";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function useIsCaseAnalyzerCompact(breakpoint = 1024) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false;

  const [isCompact, setIsCompact] = useState(getInitial());

  useEffect(() => {
    const onResize = () => setIsCompact(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isCompact;
}

const STATUS_UI = {
  queued: {
    key: "queued",
    label: "Queued",
    shortLabel: "Queued",
    text: "Your case package is waiting to start.",
    color: "#64748B",
    percent: 5,
  },
  worker_received: {
    key: "worker_received",
    label: "Received",
    shortLabel: "Received",
    text: "Your Case Analyzer request was received.",
    color: "#2563EB",
    percent: 8,
  },
  ingesting: {
    key: "ingesting",
    label: "Reading evidence",
    shortLabel: "Reading",
    text: "Your uploaded files are being opened and prepared.",
    color: "#0EA5E9",
    percent: 12,
  },
  summarizing_files: {
    key: "summarizing_files",
    label: "Summarizing files",
    shortLabel: "Summarizing",
    text: "Lexa is summarizing the evidence file by file.",
    color: "#8B5CF6",
    percent: 28,
  },
  mapping_evidence: {
    key: "mapping_evidence",
    label: "Organizing evidence",
    shortLabel: "Organizing",
    text: "Evidence is being grouped into useful case categories.",
    color: "#6366F1",
    percent: 55,
  },
  analyzing_case: {
    key: "analyzing_case",
    label: "Analyzing case",
    shortLabel: "Analyzing",
    text: "The case probability, risks, strengths, and gaps are being reviewed.",
    color: "#F59E0B",
    percent: 72,
  },
  building_report: {
    key: "building_report",
    label: "Building report",
    shortLabel: "Building",
    text: "The final Case Analyzer dashboard is being assembled.",
    color: "#EA580C",
    percent: 82,
  },
  rendering_pdf: {
    key: "rendering_pdf",
    label: "Preparing PDF",
    shortLabel: "PDF",
    text: "Your report file is being rendered.",
    color: "#D97706",
    percent: 90,
  },
  saving_report: {
    key: "saving_report",
    label: "Saving report",
    shortLabel: "Saving",
    text: "The completed report is being saved to File Vault.",
    color: "#059669",
    percent: 95,
  },
  complete: {
    key: "complete",
    label: "Ready",
    shortLabel: "Ready",
    text: "Your Case Analyzer report is ready.",
    color: "#059669",
    percent: 100,
  },
  error: {
    key: "error",
    label: "Needs attention",
    shortLabel: "Error",
    text: "The analysis could not be completed.",
    color: "#DC2626",
    percent: 100,
  },
};

const STATUS_STEPS = [
  STATUS_UI.worker_received,
  STATUS_UI.ingesting,
  STATUS_UI.summarizing_files,
  STATUS_UI.mapping_evidence,
  STATUS_UI.analyzing_case,
  STATUS_UI.building_report,
  STATUS_UI.rendering_pdf,
  STATUS_UI.saving_report,
  STATUS_UI.complete,
];

const STATUS_INDEX = STATUS_STEPS.reduce((acc, item, idx) => {
  acc[item.key] = idx;
  return acc;
}, {});

const STATUS_ALIASES = {
  intake: "queued",
  uploaded: "queued",
  dispatched: "queued",
  verifying_package: "worker_received",
  extracting: "ingesting",
  chunking: "summarizing_files",
  analyzing: "analyzing_case",
  completed: "complete",
  report_ready: "complete",
  failed: "error",
};

function normalizeStatus(rawStatus, rawProgress) {
  const raw = String(rawStatus || rawProgress?.stage || "").trim().toLowerCase();
  if (!raw) return "queued";
  return STATUS_ALIASES[raw] || raw;
}

function getStageMeta(status, progress) {
  const normalized = normalizeStatus(status, progress);
  return STATUS_UI[normalized] || STATUS_UI.queued;
}

function statusIndex(status, progress) {
  const normalized = normalizeStatus(status, progress);
  if (normalized === "error") return -1;
  if (Object.prototype.hasOwnProperty.call(STATUS_INDEX, normalized)) return STATUS_INDEX[normalized];
  return -1;
}

function getNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n, min = 0, max = 100) {
  return Math.max(min, Math.min(max, Number(n) || 0));
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
}

function pickFirstNumeric(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) return value;

    if (typeof value === "string" && value.trim() !== "") {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
  }

  return "";
}

function getByPath(obj, path, fallback = "") {
  const parts = String(path || "").split(".").filter(Boolean);
  let cur = obj;
  for (const part of parts) {
    if (cur === undefined || cur === null) return fallback;
    cur = cur?.[part];
  }
  return cur === undefined || cur === null || cur === "" ? fallback : cur;
}

function normalizeHistoryPayload(data) {
  if (!data) return {};

  if (data.history && typeof data.history === "object") return data.history;
  if (data.caseAnalyzerHistory && typeof data.caseAnalyzerHistory === "object") return data.caseAnalyzerHistory;
  if (data.case_analyzer_history && typeof data.case_analyzer_history === "object") return data.case_analyzer_history;

  if (data.report && typeof data.report === "object") {
    if (data.report.history && typeof data.report.history === "object") return data.report.history;
    return data.report;
  }

  if (data.dashboard || data.charts || data.liveView || data.caseSnapshot) return data;
  return data;
}

function normalizeDashboardPayload(data) {
  if (!data || typeof data !== "object") return {};

  const history =
    data.history && typeof data.history === "object"
      ? data.history
      : data.caseAnalyzerHistory && typeof data.caseAnalyzerHistory === "object"
      ? data.caseAnalyzerHistory
      : data.case_analyzer_history && typeof data.case_analyzer_history === "object"
      ? data.case_analyzer_history
      : {};

  const report = data.report && typeof data.report === "object" ? data.report : {};

  const dashboardArtifact =
    data.dashboardArtifact && typeof data.dashboardArtifact === "object"
      ? data.dashboardArtifact
      : data.dashboard_artifact && typeof data.dashboard_artifact === "object"
      ? data.dashboard_artifact
      : report.dashboardArtifact && typeof report.dashboardArtifact === "object"
      ? report.dashboardArtifact
      : report.dashboard_artifact && typeof report.dashboard_artifact === "object"
      ? report.dashboard_artifact
      : {};

  // Same contract as the dedicated dashboard page:
  // Firestore history may be lightweight; the real completed dashboard data may
  // come back at the top level as dashboardArtifact/dashboard/charts/liveView.
  return {
    ...history,
    ...report,
    ...data,
    history,
    report,
    dashboardArtifact,
    dashboard_artifact: dashboardArtifact,
  };
}

function objectValuesDeep(obj, maxDepth = 7) {
  const out = [];
  const seen = new Set();

  function walk(value, depth) {
    if (depth > maxDepth || value === undefined || value === null) return;
    if (typeof value !== "object") return;
    if (seen.has(value)) return;
    seen.add(value);

    if (Array.isArray(value)) {
      value.forEach((item) => walk(item, depth + 1));
      return;
    }

    out.push(value);
    Object.values(value).forEach((item) => walk(item, depth + 1));
  }

  walk(obj, 0);
  return out;
}

function normalizeKey(key) {
  return String(key || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

function firstDeepValue(obj, keys = []) {
  const targets = keys.map(normalizeKey).filter(Boolean);
  if (!targets.length) return "";

  for (const node of objectValuesDeep(obj, 7)) {
    for (const [key, value] of Object.entries(node || {})) {
      if (targets.includes(normalizeKey(key)) && value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }

  return "";
}

function firstDeepNumber(obj, keys = []) {
  const value = firstDeepValue(obj, keys);
  if (value === "" || value === undefined || value === null) return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : "";
}

function firstDeepArray(obj, keys = []) {
  const targets = keys.map(normalizeKey).filter(Boolean);
  for (const node of objectValuesDeep(obj, 7)) {
    for (const [key, value] of Object.entries(node || {})) {
      if (targets.includes(normalizeKey(key)) && Array.isArray(value) && value.length) return value;
    }
  }
  return [];
}

function firstDeepObject(obj, keys = []) {
  const targets = keys.map(normalizeKey).filter(Boolean);
  for (const node of objectValuesDeep(obj, 7)) {
    for (const [key, value] of Object.entries(node || {})) {
      if (targets.includes(normalizeKey(key)) && value && typeof value === "object" && !Array.isArray(value)) return value;
    }
  }
  return {};
}

function scoreDashboardObject(...sources) {
  for (const source of sources) {
    const candidate =
      source?.scoreDashboard ||
      source?.score_dashboard ||
      source?.dashboardScores ||
      source?.dashboard_scores ||
      source?.scores ||
      source?.metrics;
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) return candidate;
  }

  return firstDeepObject({ sources }, [
    "scoreDashboard",
    "score_dashboard",
    "dashboardScores",
    "dashboard_scores",
    "scores",
    "metrics",
  ]);
}

function scoreFromScoreDashboard(scoreDashboard, keys = []) {
  if (!scoreDashboard || typeof scoreDashboard !== "object") return "";

  const direct = pickFirst(...keys.map((key) => scoreDashboard?.[key]));
  if (direct !== "" && direct !== undefined && direct !== null) {
    const n = Number(direct);
    if (Number.isFinite(n)) return n;
  }

  const targets = keys.map(normalizeKey);
  const items = asArray(scoreDashboard.items || scoreDashboard.rows || scoreDashboard.metrics || scoreDashboard.data);
  for (const item of items) {
    const label = normalizeKey(item?.label || item?.title || item?.name || item?.key || item?.metric);
    if (targets.some((target) => label.includes(target) || target.includes(label))) {
      const n = Number(
        item?.value ??
          item?.value_0_to_100 ??
          item?.raw_value ??
          item?.score_0_to_100 ??
          item?.score ??
          item?.percent ??
          item?.rating
      );
      if (Number.isFinite(n)) return n;
    }
  }

  return firstDeepNumber(scoreDashboard, keys);
}


function dashboardSourcesFromPayload(data) {
  const report = normalizeDashboardPayload(data);
  const dashboardArtifact = report?.dashboardArtifact || report?.dashboard_artifact || {};
  const dashboard =
    report?.dashboard ||
    report?.dashboardData ||
    report?.dashboard_data ||
    dashboardArtifact?.dashboard ||
    dashboardArtifact?.dashboardData ||
    dashboardArtifact?.dashboard_data ||
    {};
  const charts =
    report?.charts ||
    report?.chartData ||
    report?.chart_data ||
    dashboardArtifact?.charts ||
    dashboardArtifact?.chartData ||
    dashboardArtifact?.chart_data ||
    dashboard?.charts ||
    {};
  const liveView =
    report?.liveView ||
    report?.live_view ||
    dashboardArtifact?.liveView ||
    dashboardArtifact?.live_view ||
    dashboard?.liveView ||
    dashboard?.live_view ||
    firstDeepObject({ report, dashboardArtifact, dashboard }, ["liveView", "live_view"]) ||
    {};
  const dashboardSummary =
    dashboard?.dashboardSummary ||
    dashboard?.dashboard_summary ||
    report?.dashboardSummary ||
    report?.dashboard_summary ||
    dashboardArtifact?.dashboardSummary ||
    dashboardArtifact?.dashboard_summary ||
    {};

  return { report, dashboardArtifact, dashboard, charts, liveView, dashboardSummary };
}

function hasUsableDashboardPayload(data) {
  const { report, dashboardArtifact, dashboard, charts, liveView, dashboardSummary } = dashboardSourcesFromPayload(data);
  const source = { report, dashboardArtifact, dashboard, charts, liveView, dashboardSummary };

  const scoreItems = asArray(
    charts?.scoreDashboard?.items ||
      charts?.score_dashboard?.items ||
      charts?.rawChartData?.score_dashboard?.items ||
      charts?.rawChartData?.scoreDashboard?.items ||
      report?.chart_data?.score_dashboard?.items ||
      report?.chartData?.scoreDashboard?.items
  );
  const riskItems = asArray(
    charts?.riskBreakdown?.items ||
      charts?.risk_breakdown?.items ||
      charts?.rawChartData?.risk_breakdown?.items ||
      report?.chart_data?.risk_breakdown?.items
  );
  const evidenceItems = asArray(
    charts?.evidenceSufficiency?.items ||
      charts?.evidence_sufficiency?.items ||
      charts?.rawChartData?.evidence_sufficiency?.items ||
      report?.evidence_sufficiency_matrix ||
      report?.evidenceSufficiencyMatrix
  );
  const missingItems = asArray(
    charts?.missingEvidencePriority?.items ||
      charts?.missing_evidence_priority?.items ||
      charts?.rawChartData?.missing_evidence_priority?.items ||
      dashboardSummary?.top_missing_evidence ||
      dashboardSummary?.topMissingEvidence
  );
  const documentItems = asArray(
    dashboard?.documentFindings ||
      dashboard?.document_findings ||
      liveView?.documentFindings ||
      liveView?.document_findings ||
      report?.reviewedDocuments ||
      report?.reviewed_documents ||
      firstDeepArray(source, ["documentFindings", "document_findings", "reviewedDocuments", "reviewed_documents"])
  );
  const strengths = asArray(
    dashboardSummary?.top_strengths ||
      dashboardSummary?.topStrengths ||
      dashboard?.keyStrengths ||
      dashboard?.key_strengths ||
      liveView?.keyStrengths ||
      liveView?.key_strengths ||
      firstDeepArray(source, ["topStrengths", "top_strengths", "keyStrengths", "key_strengths"])
  );
  const risks = asArray(
    dashboardSummary?.top_risks ||
      dashboardSummary?.topRisks ||
      dashboard?.topRisks ||
      dashboard?.top_risks ||
      liveView?.topRisks ||
      liveView?.top_risks ||
      firstDeepArray(source, ["topRisks", "top_risks", "keyRisks", "key_risks"])
  );

  // Summary-only payloads are not enough. The status page should keep
  // retrying until it has the same useful dashboard artifact data that the
  // dedicated dashboard page displays: scores, risk/evidence tables, missing
  // evidence, or document findings.
  return Boolean(
    scoreItems.length >= 4 ||
      riskItems.length >= 2 ||
      evidenceItems.length >= 2 ||
      missingItems.length >= 2 ||
      documentItems.length >= 1
  );
}

function openJsonPayloadInNewTab(payload, filename = "case_analyzer_dashboard.json") {
  if (!payload) return;

  try {
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const tab = window.open(url, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(url), tab ? 60000 : 5000);
  } catch (err) {
    const text = JSON.stringify(payload, null, 2);
    const blob = new Blob([text], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return [];
  return [value];
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isProgrammingLikeText(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (/^\{[\s\S]*\}$/.test(text) || /^\[[\s\S]*\]$/.test(text)) return true;
  if (/^```/.test(text)) return true;
  if (/\b(true|false|null|None)\b/.test(text) && /[{}:[\],]/.test(text)) return true;
  if (/(^|[,\s])['"]?[a-zA-Z0-9_]+['"]?\s*:\s*['"]?/.test(text) && /[{}]/.test(text)) return true;
  return false;
}

function cleanDisplayText(value, fallback = "—") {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const joined = value.map((item) => cleanDisplayText(item, "")).filter(Boolean).join(", ");
    return joined || fallback;
  }
  if (isPlainObject(value)) return normalizeTextItem(value) || fallback;
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  if (isProgrammingLikeText(text)) return fallback;
  return text;
}

function normalizeTextItem(item) {
  if (typeof item === "string") {
    return isProgrammingLikeText(item) ? "" : item.replace(/\s+/g, " ").trim();
  }
  if (!item || typeof item !== "object") return String(item || "").trim();

  const preferredKeys = [
    "text",
    "summary",
    "title",
    "heading",
    "label",
    "name",
    "event",
    "risk",
    "strength",
    "weakness",
    "missing_item",
    "missingItem",
    "item",
    "question",
    "note",
    "explanation",
    "reason",
    "why_needed",
    "whyNeeded",
    "why_it_matters",
    "whyItMatters",
    "finding",
    "issue",
    "argument",
  ];

  for (const key of preferredKeys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && value !== "" && typeof value !== "object") {
      const text = cleanDisplayText(value, "");
      if (text) return text;
    }
  }

  const pieces = [];
  Object.entries(item || {}).forEach(([key, value]) => {
    if (pieces.length >= 4) return;
    if (value === undefined || value === null || value === "" || typeof value === "object") return;
    const text = cleanDisplayText(value, "");
    if (text) pieces.push(`${String(key).replace(/_/g, " ")}: ${text}`);
  });

  return pieces.join(" • ");
}

function compactDisplayItems(items, limit = 8) {
  return asArray(items)
    .map((item) => {
      if (isPlainObject(item)) {
        return {
          text: normalizeTextItem(item),
          priority: cleanDisplayText(item.priority || item.severity || item.confidence, ""),
          source: cleanDisplayText(item.source_reference || item.source || item.document || item.file, ""),
          reason: cleanDisplayText(item.reason || item.why_needed || item.whyNeeded || item.explanation, ""),
        };
      }
      return { text: normalizeTextItem(item), priority: "", source: "", reason: "" };
    })
    .filter((item) => item.text)
    .slice(0, limit);
}

function getCaseTypeFramework(report, liveView = {}) {
  return (
    report?.caseTypeFramework ||
    report?.case_type_framework ||
    liveView?.caseTypeFramework ||
    liveView?.case_type_framework ||
    {}
  );
}

function getPlaybookUsage(report, liveView = {}) {
  return (
    report?.playbookUsageSummary ||
    report?.playbook_usage_summary ||
    liveView?.playbookUsageSummary ||
    liveView?.playbook_usage_summary ||
    {}
  );
}

function normalizeEvidencePlaybookRows(playbookUsage = {}) {
  const rows = asArray(
    playbookUsage?.evidenceBundles ||
      playbookUsage?.evidence_bundle_playbooks ||
      playbookUsage?.evidenceBundlePlaybooks ||
      playbookUsage?.files ||
      []
  );

  return rows
    .filter((item) => isPlainObject(item))
    .map((item, idx) => ({
      key: item.fileId || item.file_id || item.fileName || item.file_name || idx,
      fileName: cleanDisplayText(item.fileName || item.file_name || item.original_name || item.name || `Evidence file ${idx + 1}`),
      selected: cleanDisplayText(item.selectedBundleLabel || item.selected_bundle_label || item.selectedBundleId || item.selected_bundle_id),
      resolved: cleanDisplayText(item.resolvedBundleLabel || item.resolved_bundle_label || item.resolvedBundleId || item.resolved_bundle_id),
      playbookPath: cleanDisplayText(item.playbookPath || item.playbook_path || item.evidence_playbook_path, ""),
      routerUsed: Boolean(item.routerUsed || item.router_used || item.router_called || item.evidence_router_used),
      confidence: cleanDisplayText(item.routerConfidence || item.router_confidence || item.confidence, ""),
      reason: cleanDisplayText(item.routerReason || item.router_reason || item.reason || item.routing_warning, ""),
    }));
}

function isSignedOrHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || "").trim());
}

function formatStoragePath(path) {
  const text = String(path || "").trim();
  if (!text) return "";
  if (text.length <= 72) return text;
  return `${text.slice(0, 34)}…${text.slice(-30)}`;
}

function escapeChartText(value) {
  return String(value ?? "")
    .replace(/\r?\n/g, " ")
    .replace(/[|;]/g, ",")
    .replace(/\s*\/\s*/g, " /\u200B ")
    .replace(/\s+/g, " ")
    .trim();
}

function niceVisualTitle(value, fallback = "Dashboard visual") {
  const text = String(value || fallback)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return fallback;

  return text
    .split(" ")
    .map((word) => {
      if (/^(AI|PDF|JSON|GCS|URL|ID)$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

function scoreItemsToChartLine(title, items, options = {}) {
  const rows = asArray(items).filter(Boolean).slice(0, options.limit || 12);
  if (!rows.length) return "";

  const labels = [];
  const values = [];

  rows.forEach((item, idx) => {
    const label = escapeChartText(
      item?.label ||
      item?.title ||
      item?.name ||
      item?.key ||
      item?.metric ||
      `Metric ${idx + 1}`
    );
    const numericValue = numericValueFromItem(
      item,
      options.valueKeys || ["value", "value_0_to_100", "raw_value", "score_0_to_100", "score", "percent", "rating"]
    );
    if (!label || numericValue === "") return;

    labels.push(label);
    values.push(clamp(numericValue));
  });

  if (!labels.length || !values.length) return "";

  const chartType = options.chartType || "bar";
  const colorInstruction =
    Array.isArray(options.colors) && options.colors.length
      ? `; colors=${options.colors.join(",")}`
      : "";

  return `[chart] type=${chartType}; title=${escapeChartText(niceVisualTitle(title))}; labels=${labels.join("|")}; values=${values.join("|")}${colorInstruction}`;
}

function bundleItemsToChartLine(items) {
  const rows = asArray(items).filter(Boolean).slice(0, 10);
  if (!rows.length) return "";

  const labels = [];
  const values = [];

  rows.forEach((item, idx) => {
    labels.push(escapeChartText(item?.label || item?.bundleLabel || item?.bundle_id || item?.bundleId || `Bundle ${idx + 1}`));
    values.push(clamp(item?.score ?? item?.value ?? item?.strengthScore ?? item?.strength_score ?? item?.fileCount ?? item?.file_count ?? 0));
  });

  return `[chart] type=bar; title=Evidence bundle comparison; labels=${labels.join("|")}; values=${values.join("|")}`;
}

function listToMarkdown(title, items, limit = 8) {
  const rows = asArray(items)
    .map(normalizeTextItem)
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, limit);

  if (!rows.length) return "";
  return [`### ${title}`, ...rows.map((item) => `- ${item}`)].join("\n");
}


function isFiniteNumberLike(value) {
  if (value === undefined || value === null || value === "") return false;
  const n = Number(value);
  return Number.isFinite(n);
}

function numericValueFromItem(item, keys = ["value", "value_0_to_100", "raw_value", "score_0_to_100", "score", "percent", "rating", "count"]) {
  if (!item || typeof item !== "object") return "";
  for (const key of keys) {
    const value = item?.[key];
    if (isFiniteNumberLike(value)) return Number(value);
  }
  return "";
}

function directChartItems(...sources) {
  for (const source of sources) {
    const items = asArray(source?.items || source?.rows || source?.metrics || source?.data);
    const usable = items
      .filter((item) => item && typeof item === "object")
      .map((item, idx) => ({
        label: cleanDisplayText(item.label || item.title || item.name || item.key || item.metric || item.issue || `Item ${idx + 1}`),
        value: numericValueFromItem(item),
        rating: cleanDisplayText(item.support_level || item.rating || item.level || item.band || item.status || item.risk_level, ""),
        source: item,
      }))
      .filter((item) => item.label && item.value !== "");

    if (usable.length) return usable;
  }

  return [];
}

function scoreItemsFromDirectKeys(source, definitions) {
  if (!source || typeof source !== "object") return [];

  return definitions
    .map(([label, ...keys]) => {
      const value = pickFirst(...keys.map((key) => getByPath(source, key)));
      return isFiniteNumberLike(value) ? { label, value: Number(value) } : null;
    })
    .filter(Boolean);
}

function buildDetailedScoreVisualItems({ scoreDashboard, charts, dashboardArtifact, report, dashboard, probabilityScore, evidenceScore, reliabilityScore, settlementScore }) {
  const directItems = directChartItems(
    charts?.scoreDashboard,
    charts?.score_dashboard,
    charts?.rawChartData?.score_dashboard,
    charts?.rawChartData?.scoreDashboard,
    report?.chart_data?.score_dashboard,
    report?.chartData?.scoreDashboard,
    dashboardArtifact?.charts?.scoreDashboard,
    dashboardArtifact?.charts?.score_dashboard,
    scoreDashboard
  );

  if (directItems.length >= 4) return directItems.slice(0, 14);

  const definitions = [
    ["Evidence Strength", "evidence_strength", "evidenceStrength"],
    ["Evidence Reliability", "evidence_reliability", "evidenceReliability"],
    ["Claim / Position Support", "claim_or_position_support", "claimOrPositionSupport"],
    ["Other Side Risk", "other_side_risk", "otherSideRisk"],
    ["Damages Support", "damages_support", "damagesSupport"],
    ["Timeline Strength", "timeline_strength", "timelineStrength"],
    ["Procedural / Deadline Risk", "procedural_deadline_risk", "proceduralDeadlineRisk"],
    ["Missing Evidence Risk", "missing_evidence_risk", "missingEvidenceRisk"],
    ["Contradiction Risk", "contradiction_risk", "contradictionRisk"],
    ["Settlement Leverage", "settlement_leverage", "settlementLeverage"],
    ["Score Reliability", "score_reliability", "scoreReliability", "scoreReliabilityScore", "score_reliability_score"],
  ];

  const fromScoreDashboard = scoreItemsFromDirectKeys(scoreDashboard, definitions);
  if (fromScoreDashboard.length) return fromScoreDashboard;

  const fromReport = scoreItemsFromDirectKeys(report?.score_dashboard, definitions);
  if (fromReport.length) return fromReport;

  return [
    ["Evidence Strength", dashboard?.evidenceStrength || dashboard?.evidence_strength || evidenceScore],
    ["Evidence Reliability", dashboard?.evidenceReliability || dashboard?.evidence_reliability],
    ["Claim / Position Support", dashboard?.claimOrPositionSupport || dashboard?.claim_or_position_support],
    ["Other Side Risk", dashboard?.otherSideRisk || dashboard?.other_side_risk],
    ["Damages Support", dashboard?.damagesSupport || dashboard?.damages_support],
    ["Timeline Strength", dashboard?.timelineStrength || dashboard?.timeline_strength],
    ["Procedural / Deadline Risk", dashboard?.proceduralDeadlineRisk || dashboard?.procedural_deadline_risk],
    ["Missing Evidence Risk", dashboard?.missingEvidenceRisk || dashboard?.missing_evidence_risk],
    ["Contradiction Risk", dashboard?.contradictionRisk || dashboard?.contradiction_risk],
    ["Settlement Leverage", pickFirstNumeric(dashboard?.settlement_leverage, dashboard?.settlementLeverage, settlementScore)],
    ["Score Reliability", dashboard?.scoreReliabilityScore || dashboard?.score_reliability_score || reliabilityScore],
  ]
    .map(([label, value]) => (isFiniteNumberLike(value) ? { label, value: Number(value) } : null))
    .filter(Boolean);
}

function buildRiskVisualItems({ charts, dashboardArtifact, report, dashboard, scoreDashboard }) {
  const directItems = directChartItems(
    charts?.riskBreakdown,
    charts?.risk_breakdown,
    charts?.rawChartData?.risk_breakdown,
    charts?.rawChartData?.riskBreakdown,
    charts?.raw_chart_data?.risk_breakdown,
    charts?.raw_chart_data?.riskBreakdown,
    report?.chart_data?.risk_breakdown,
    dashboardArtifact?.charts?.riskBreakdown,
    dashboardArtifact?.charts?.risk_breakdown,
    dashboardArtifact?.charts?.rawChartData?.risk_breakdown,
    dashboardArtifact?.charts?.rawChartData?.riskBreakdown,
    dashboardArtifact?.charts?.raw_chart_data?.risk_breakdown,
    dashboardArtifact?.charts?.raw_chart_data?.riskBreakdown,
    dashboard?.charts?.riskBreakdown,
    dashboard?.charts?.risk_breakdown,
    dashboard?.riskBreakdown,
    dashboard?.risk_breakdown,
    report?.charts?.riskBreakdown,
    report?.charts?.risk_breakdown
  );
  if (directItems.length) return directItems.slice(0, 10);

  const definitions = [
    ["Evidence Reliability", "evidence_reliability", "evidenceReliability"],
    ["Missing Evidence Impact", "missing_evidence_risk", "missingEvidenceRisk"],
    ["Damages Proof Risk", "damages_support", "damagesSupport"],
    ["Procedural / Deadline Risk", "procedural_deadline_risk", "proceduralDeadlineRisk"],
    ["Contradiction / Defense Risk", "contradiction_risk", "contradictionRisk"],
    ["Other Side Risk", "other_side_risk", "otherSideRisk"],
  ];

  const fromScore = scoreItemsFromDirectKeys(scoreDashboard, definitions);
  if (fromScore.length) return fromScore.slice(0, 8);

  const fromReport = scoreItemsFromDirectKeys(report?.score_dashboard, definitions);
  if (fromReport.length) return fromReport.slice(0, 8);

  return [
    ["Missing Evidence Impact", dashboard?.missingEvidenceRisk || dashboard?.missing_evidence_risk],
    ["Procedural / Deadline Risk", dashboard?.proceduralDeadlineRisk || dashboard?.procedural_deadline_risk],
    ["Contradiction / Defense Risk", dashboard?.contradictionRisk || dashboard?.contradiction_risk],
    ["Other Side Risk", dashboard?.otherSideRisk || dashboard?.other_side_risk],
  ]
    .map(([label, value]) => (isFiniteNumberLike(value) ? { label, value: Number(value) } : null))
    .filter(Boolean);
}

function buildOutcomeRangeItems({ report, dashboard, dashboardArtifact }) {
  const allSource = { report, dashboard, dashboardArtifact };

  function rangeFromObject(value) {
    if (!value) return "";
    if (typeof value === "string" && value.trim()) return value.trim();

    if (Array.isArray(value) && value.length >= 2 && isFiniteNumberLike(value[0]) && isFiniteNumberLike(value[1])) {
      return `${Number(value[0])}–${Number(value[1])}`;
    }

    if (typeof value === "object") {
      const low = pickFirst(
        value.low,
        value.min,
        value.from,
        value.start,
        value.lower,
        value.lower_bound,
        value.lowerBound
      );
      const high = pickFirst(
        value.high,
        value.max,
        value.to,
        value.end,
        value.upper,
        value.upper_bound,
        value.upperBound
      );

      if (isFiniteNumberLike(low) && isFiniteNumberLike(high)) {
        return `${Number(low)}–${Number(high)}`;
      }

      if (value.value !== undefined && value.value !== null) {
        return cleanDisplayText(value.value, "");
      }
    }

    return "";
  }

  function deepRangeByNormalizedKeys(targetKeys) {
    const targets = targetKeys.map(normalizeKey).filter(Boolean);

    for (const node of objectValuesDeep(allSource, 8)) {
      for (const [key, value] of Object.entries(node || {})) {
        if (!targets.includes(normalizeKey(key))) continue;
        const range = rangeFromObject(value);
        if (range) return range;
      }
    }

    return "";
  }

  const directRanges = asArray(
    dashboard?.outcomeRanges ||
      dashboard?.outcome_ranges ||
      dashboardArtifact?.outcomeRanges ||
      dashboardArtifact?.outcome_ranges ||
      dashboardArtifact?.dashboard?.outcomeRanges ||
      dashboardArtifact?.dashboard?.outcome_ranges ||
      report?.outcomeRanges ||
      report?.outcome_ranges ||
      report?.probability_profile?.outcome_ranges ||
      report?.probability?.outcome_ranges
  );

  if (directRanges.length) {
    const mapped = directRanges
      .map((item) => {
        if (!item || typeof item === "string") return null;
        const label = cleanDisplayText(
          item.label || item.title || item.name || item.rangeLabel || item.range_label,
          ""
        );
        const value = rangeFromObject(item.value || item.range || item);
        if (!label || !value) return null;
        return { label, value };
      })
      .filter(Boolean);

    if (mapped.length) return mapped.slice(0, 3);
  }

  return [
    {
      label: "Favorable outcome range",
      value: deepRangeByNormalizedKeys([
        "favorableOutcomeRange",
        "favorable_outcome_range",
        "favorableRange",
        "favorable_range",
        "favorable",
      ]),
    },
    {
      label: "Mixed / uncertain range",
      value: deepRangeByNormalizedKeys([
        "mixedUncertainRange",
        "mixed_uncertain_range",
        "mixedRange",
        "mixed_range",
        "mixed",
        "uncertainRange",
        "uncertain_range",
      ]),
    },
    {
      label: "Adverse risk range",
      value: deepRangeByNormalizedKeys([
        "adverseRiskRange",
        "adverse_risk_range",
        "adverseRange",
        "adverse_range",
        "adverse",
        "riskRange",
        "risk_range",
      ]),
    },
  ].filter((item) => item.value);
}

function buildEvidenceSufficiencyItems({ charts, dashboardArtifact, report, dashboard }) {
  const directItems = directChartItems(
    charts?.evidenceSufficiency,
    charts?.evidence_sufficiency,
    charts?.rawChartData?.evidence_sufficiency,
    report?.chart_data?.evidence_sufficiency,
    dashboardArtifact?.charts?.evidenceSufficiency,
    dashboardArtifact?.charts?.evidence_sufficiency,
    report?.evidence_sufficiency,
    report?.evidenceSufficiency,
    dashboard?.evidenceSufficiency,
    dashboard?.evidence_sufficiency,
    report?.evidence_sufficiency_matrix,
    report?.evidenceSufficiencyMatrix
  );

  if (directItems.length) return directItems.slice(0, 12);

  const rows = asArray(
    report?.evidence_sufficiency_matrix ||
      report?.evidenceSufficiencyMatrix ||
      report?.evidence_sufficiency ||
      report?.evidenceSufficiency ||
      dashboardArtifact?.evidenceSufficiency ||
      dashboardArtifact?.evidence_sufficiency ||
      dashboard?.evidenceSufficiency ||
      dashboard?.evidence_sufficiency
  );

  return rows
    .filter((item) => item && typeof item === "object")
    .map((item, idx) => ({
      label: cleanDisplayText(item.issue || item.issue_or_element || item.element || item.label || item.title || item.name || `Evidence item ${idx + 1}`),
      value: numericValueFromItem(item, ["value", "value_0_to_100", "score", "score_0_to_100", "percent", "rating_score"]),
      rating: cleanDisplayText(item.support_level || item.current_support || item.rating || item.level || item.band || item.status || item.risk_level, ""),
      source: item,
    }))
    .filter((item) => item.label && item.value !== "")
    .slice(0, 12);
}

function buildPriorityEvidenceItems({ charts, dashboardArtifact, dashboard, liveView, report }) {
  return asArray(
    charts?.missingEvidencePriority?.items ||
      charts?.missing_evidence_priority?.items ||
      charts?.rawChartData?.missing_evidence_priority?.items ||
      report?.chart_data?.missing_evidence_priority?.items ||
      dashboardArtifact?.charts?.missingEvidencePriority?.items ||
      dashboardArtifact?.charts?.missing_evidence_priority?.items ||
      dashboard?.dashboardSummary?.top_missing_evidence ||
      dashboard?.dashboardSummary?.topMissingEvidence ||
      dashboard?.dashboard_summary?.top_missing_evidence ||
      dashboard?.topMissingEvidence ||
      dashboard?.top_missing_evidence ||
      liveView?.missingEvidence ||
      liveView?.missing_evidence ||
      firstDeepArray({ report, dashboardArtifact, dashboard, liveView }, ["missingEvidencePriority", "missing_evidence_priority", "topMissingEvidence", "top_missing_evidence", "missingEvidence", "missing_evidence"])
  ).slice(0, 10);
}
function buildScoreDashboardItems({ probabilityScore, evidenceScore, reliabilityScore, settlementScore }) {
  const rows = [
    { label: "Outcome posture", value: probabilityScore },
    { label: "Evidence strength", value: evidenceScore },
    { label: "Reliability", value: reliabilityScore },
    { label: "Settlement leverage", value: settlementScore },
  ];

  return rows.filter((row) => isFiniteNumberLike(row.value));
}


function bundleItemsToTable(items) {
  const rows = asArray(items).filter((item) => item && typeof item === "object").slice(0, 20);
  if (!rows.length) return "";

  const normalizedRows = rows.map((item, idx) => {
    const label = cleanDisplayText(
      item.label ||
        item.bundleLabel ||
        item.bundle_label ||
        item.bundleId ||
        item.bundle_id ||
        item.category ||
        `Bundle ${idx + 1}`,
      `Bundle ${idx + 1}`
    );

    return {
      bundle: label,
      files: isFiniteNumberLike(item.fileCount ?? item.file_count) ? Number(item.fileCount ?? item.file_count) : "—",
      strengths: isFiniteNumberLike(item.strengthCount ?? item.strength_count) ? Number(item.strengthCount ?? item.strength_count) : "—",
      risks: isFiniteNumberLike(item.riskOrGapCount ?? item.risk_or_gap_count ?? item.riskCount ?? item.risk_count)
        ? Number(item.riskOrGapCount ?? item.risk_or_gap_count ?? item.riskCount ?? item.risk_count)
        : "—",
      missing: isFiniteNumberLike(item.missingEvidenceCount ?? item.missing_evidence_count)
        ? Number(item.missingEvidenceCount ?? item.missing_evidence_count)
        : "—",
      effect: cleanDisplayText(item.overallEffect || item.overall_effect || item.effect, ""),
    };
  });

  if (!normalizedRows.length) return "";

  const lines = [
    "### Evidence bundle comparison",
    "| Evidence bundle | Files | Strengths | Risks / gaps | Missing evidence | Overall effect |",
    "|---|---:|---:|---:|---:|---|",
  ];

  normalizedRows.forEach((row) => {
    lines.push(
      `| ${escapeTableCell(row.bundle)} | ${escapeTableCell(row.files)} | ${escapeTableCell(row.strengths)} | ${escapeTableCell(row.risks)} | ${escapeTableCell(row.missing)} | ${escapeTableCell(row.effect || "—")} |`
    );
  });

  return lines.join("\n");
}


function escapeTableCell(value) {
  return String(value ?? "")
    .replace(/\r?\n/g, " ")
    .replace(/\|/g, "\\|")
    .trim();
}


function buildTimelineTable(items) {
  const rows = asArray(items).filter(Boolean).slice(0, 20);
  if (!rows.length) return "";

  const normalizedRows = rows.map((item, idx) => {
    if (typeof item === "string") {
      return { order: idx + 1, date: "—", event: item, source: "—" };
    }
    return {
      order: item?.index || idx + 1,
      date: cleanDisplayText(item?.date || item?.date_or_period || item?.period, "—"),
      event: normalizeTextItem(item) || cleanDisplayText(item?.event || item?.text || item?.summary, "—"),
      source: cleanDisplayText(item?.source || item?.source_reference || item?.document, "—"),
    };
  });

  const lines = [
    "### Timeline highlights",
    "| # | Date / period | Event | Source |",
    "|---:|---|---|---|",
  ];

  normalizedRows.forEach((row) => {
    lines.push(`| ${escapeTableCell(row.order)} | ${escapeTableCell(row.date)} | ${escapeTableCell(row.event)} | ${escapeTableCell(row.source)} |`);
  });

  return lines.join("\n");
}


function buildMissingEvidenceTable(items) {
  const rows = asArray(items).filter(Boolean).slice(0, 20);
  if (!rows.length) return "";

  const hasPriorityOrReason = rows.some(
    (item) =>
      item &&
      typeof item === "object" &&
      (item.priority || item.severity || item.reason || item.why_needed || item.whyNeeded || item.source_reference || item.source)
  );

  if (!hasPriorityOrReason) {
    return listToMarkdown("Missing evidence", rows, 20);
  }

  const lines = [
    "### Missing evidence",
    "| Item | Priority / severity | Why it matters | Source |",
    "|---|---|---|---|",
  ];

  rows.forEach((item) => {
    if (typeof item === "string") {
      lines.push(`| ${escapeTableCell(item)} | — | — | — |`);
      return;
    }

    const itemText = cleanDisplayText(
      item?.missing_item || item?.missingItem || item?.item || item?.question || item?.title || item?.text || normalizeTextItem(item),
      "—"
    );
    const priority = cleanDisplayText(item?.priority || item?.severity || item?.confidence, "—");
    const reason = cleanDisplayText(item?.reason || item?.why_needed || item?.whyNeeded || item?.why_it_matters || item?.whyItMatters, "—");
    const source = cleanDisplayText(item?.source_reference || item?.source || item?.document, "—");
    lines.push(`| ${escapeTableCell(itemText)} | ${escapeTableCell(priority)} | ${escapeTableCell(reason)} | ${escapeTableCell(source)} |`);
  });

  return lines.join("\n");
}


function buildRendererText({ report, dashboard, charts, liveView, probabilityBand, dashboardArtifact }) {
  const blocks = [];
  const allSource = { report, dashboard, charts, liveView, dashboardArtifact };

  const summary =
    liveView?.executiveSummary ||
    liveView?.executive_summary ||
    dashboard?.executiveSummary ||
    dashboard?.executive_summary ||
    dashboard?.summary ||
    report?.summary ||
    getByPath(report, "executive_summary.summary") ||
    getByPath(report, "executive_summary.case_posture_summary") ||
    firstDeepValue(allSource, [
      "case_posture_summary",
      "casePostureSummary",
      "executiveSummary",
      "executive_summary",
      "downloadReadySummary",
      "download_ready_summary",
      "summary",
    ]) ||
    dashboard?.probabilityExplanation ||
    dashboard?.probability_explanation ||
    "";

  // Summary is already displayed in the Dashboard summary panel above.

  const scoreDashboard = scoreDashboardObject(report, dashboard, charts, liveView, dashboardArtifact);

  const probabilityScore = pickFirst(
    dashboard?.probabilityScore,
    dashboard?.probability_score,
    report?.probabilityScore,
    report?.probability_score,
    getByPath(report, "probability_profile.probability_score_0_to_100"),
    getByPath(report, "probability_profile.score_0_to_100"),
    getByPath(report, "probability.score_0_to_100"),
    getByPath(report, "probability.score"),
    scoreFromScoreDashboard(scoreDashboard, ["probabilityScore", "probability_score", "Outcome posture", "Outcome Profile", "probability_score_0_to_100"]),
    firstDeepNumber(allSource, ["probabilityScore", "probability_score", "probability_score_0_to_100", "score_0_to_100"])
  );

  const evidenceScore = pickFirst(
    dashboard?.evidenceStrength,
    dashboard?.evidence_strength,
    report?.evidenceStrength,
    report?.evidence_strength,
    scoreFromScoreDashboard(scoreDashboard, ["evidenceStrength", "evidence_strength", "Evidence Strength"]),
    getByPath(report, "score_dashboard.evidence_strength"),
    firstDeepNumber(allSource, ["evidenceStrength", "evidence_strength"])
  );

  const reliabilityScore = pickFirst(
    dashboard?.scoreReliabilityScore,
    dashboard?.score_reliability_score,
    dashboard?.evidenceReliability,
    dashboard?.evidence_reliability,
    report?.scoreReliabilityScore,
    report?.score_reliability_score,
    report?.evidenceReliability,
    report?.evidence_reliability,
    scoreFromScoreDashboard(scoreDashboard, ["scoreReliability", "score_reliability", "scoreReliabilityScore", "score_reliability_score", "Evidence Reliability", "Score Reliability", "Reliability"]),
    getByPath(report, "score_dashboard.score_reliability"),
    getByPath(report, "score_reliability.score"),
    firstDeepNumber(allSource, ["scoreReliability", "score_reliability", "scoreReliabilityScore", "score_reliability_score", "evidenceReliability", "evidence_reliability"])
  );

  const settlementScore = pickFirstNumeric(
    dashboard?.settlement_leverage,
    report?.settlement_leverage,
    getByPath(report, "score_dashboard.settlement_leverage"),
    scoreFromScoreDashboard(scoreDashboard, ["settlement_leverage", "Settlement Leverage"]),
    dashboard?.settlementLeverage,
    report?.settlementLeverage
  );

  const topScoreItems = buildScoreDashboardItems({
    probabilityScore,
    evidenceScore,
    reliabilityScore,
    settlementScore,
  });

  const topScoreLine = scoreItemsToChartLine(
    "Case Analyzer Score Dashboard",
    topScoreItems,
    {
      colors: ["#2563EB", "#059669", "#7C3AED", "#EA580C"],
    }
  );
  if (topScoreLine) {
    blocks.push(topScoreLine);
  }

  const riskBreakdownItems = asArray(
    charts?.riskBreakdown?.items ||
      charts?.risk_breakdown?.items ||
      dashboardArtifact?.charts?.riskBreakdown?.items ||
      dashboardArtifact?.charts?.risk_breakdown?.items
  );

  const riskLine = scoreItemsToChartLine("Risk Breakdown", riskBreakdownItems);
  if (riskLine) {
    blocks.push(riskLine);
  }

  const bundleComparisonItems = asArray(
    charts?.bundleComparison?.items ||
      charts?.bundle_comparison?.items ||
      dashboardArtifact?.charts?.bundleComparison?.items ||
      dashboardArtifact?.charts?.bundle_comparison?.items
  );

  const bundleLine = bundleItemsToChartLine(bundleComparisonItems);
  if (bundleLine) {
    blocks.push(bundleLine);
  }

  const bundleTable = bundleItemsToTable(bundleComparisonItems);
  if (bundleTable) {
    blocks.push(bundleTable);
  }

  const timelineItems = asArray(
    charts?.timeline?.items ||
      dashboardArtifact?.charts?.timeline?.items ||
      liveView?.timeline ||
      firstDeepArray({ liveView, dashboard }, ["timeline", "timelineHighlights", "timeline_highlights"])
  );

  const timelineTable = buildTimelineTable(timelineItems);
  if (timelineTable) {
    blocks.push(timelineTable);
  }

  const keyStrengths = asArray(
    liveView?.keyStrengths ||
      liveView?.key_strengths ||
      liveView?.topStrengths ||
      liveView?.top_strengths ||
      dashboard?.topStrengths ||
      dashboard?.top_strengths ||
      firstDeepArray({ liveView, dashboard }, ["keyStrengths", "key_strengths", "topStrengths", "top_strengths"])
  );

  const keyWeaknesses = asArray(
    liveView?.keyWeaknesses ||
      liveView?.key_weaknesses ||
      liveView?.topRisks ||
      liveView?.top_risks ||
      dashboard?.topRisks ||
      dashboard?.top_risks ||
      firstDeepArray({ liveView, dashboard }, ["keyWeaknesses", "key_weaknesses", "topRisks", "top_risks"])
  );

  const missingEvidence = asArray(
    liveView?.missingEvidence ||
      liveView?.missing_evidence ||
      dashboard?.topMissingEvidence ||
      dashboard?.top_missing_evidence ||
      firstDeepArray({ liveView, dashboard }, ["missingEvidence", "missing_evidence", "topMissingEvidence", "top_missing_evidence"])
  );

  const missingEvidenceTable = buildMissingEvidenceTable(missingEvidence);
  if (missingEvidenceTable) {
    blocks.push(missingEvidenceTable);
  }

  if (probabilityBand && probabilityBand !== "Pending") {
    blocks.push(`**Probability band:** ${probabilityBand}`);
  }

  return blocks.filter(Boolean).join("\n\n");
}


export default function CaseAnalyzerStatus() {
  const q = useQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const isCompact = useIsCaseAnalyzerCompact();
  const pollTimer = useRef(null);
  const historyMissCount = useRef(0);

  const jobId = q.get("jobId") || location.state?.jobId || "";
  const [idToken, setIdToken] = useState("");
  const [authReady, setAuthReady] = useState(false);
  const [payload, setPayload] = useState(null);
  const [reportPayload, setReportPayload] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [guardChecked, setGuardChecked] = useState(false);
  const [showAllEvidenceFiles, setShowAllEvidenceFiles] = useState(false);
  const [showProcessingDetails, setShowProcessingDetails] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIdToken("");
        setAuthReady(true);
        return;
      }

      try {
        const token = await user.getIdToken();
        setIdToken(token);
      } catch (err) {
        setError(err?.message || "Unable to verify your login session.");
      } finally {
        setAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!jobId) {
      navigate("/case-analyzer", { replace: true });
      return;
    }

    const handoffKey = `caseAnalyzerStatus:${jobId}`;
    const cameFromCaseAnalyzer =
      location.state?.fromCaseAnalyzer === true ||
      location.state?.fromCaseAnalyzerHistory === true ||
      sessionStorage.getItem(handoffKey) === "allowed";

    if (!cameFromCaseAnalyzer) {
      navigate("/case-analyzer", { replace: true });
      return;
    }

    setGuardChecked(true);
  }, [jobId, location.state, navigate]);

  const loadHistory = async () => {
    let historyData = null;

    try {
      // Preferred completed-report reader. This endpoint now loads the full
      // dashboard JSON from the bucket using dashboardJsonPath, while keeping
      // Firestore history lightweight.
      historyData = await getCaseAnalyzerDashboard(jobId, { idToken });
    } catch (dashboardErr) {
      // Fallback keeps older deployments usable during rollout.
      historyData = await getCaseAnalyzerHistory(jobId, { idToken });
      historyData = {
        ...historyData,
        dashboardArtifactError: dashboardErr?.message || "",
        dashboard_artifact_error: dashboardErr?.message || "",
      };
    }

    setReportPayload(historyData);
    return historyData;
  };

  const fetchStatus = async () => {
    if (!jobId || !idToken) return;

    try {
      const data = await getCaseAnalyzerStatus(jobId, { idToken });
      setPayload(data);
      setLoading(false);
      setError("");

      const status = data?.status || data?.job?.status;
      const progress = data?.progress || data?.job?.progress || {};
      const normalized = normalizeStatus(status, progress);

      if (normalized === "complete") {
        try {
          const historyData = await loadHistory();
          const dashboardReady = hasUsableDashboardPayload(historyData);

          // The dashboard endpoint can return a lightweight summary before the
          // full bucket dashboard JSON is fully readable. Do not stop just
          // because a summary exists; keep retrying until charts/tables/findings
          // are present, so users do not need to manually refresh on web/mobile.
          if (!dashboardReady && historyMissCount.current < 24) {
            historyMissCount.current += 1;
            pollTimer.current = setTimeout(fetchStatus, 2500);
          }
        } catch (err) {
          historyMissCount.current += 1;
          setError(err?.message || "Report is ready, but the report dashboard could not be loaded yet.");
          if (historyMissCount.current < 24) {
            pollTimer.current = setTimeout(fetchStatus, 3500);
          }
        }
        return;
      }

      if (normalized === "error") {
        const message =
          data?.error?.message ||
          data?.error ||
          data?.job?.error?.message ||
          data?.job?.error ||
          progress?.message ||
          "The Case Analyzer job failed.";
        setError(String(message));
        return;
      }

      pollTimer.current = setTimeout(fetchStatus, 2500);
    } catch (err) {
      setLoading(false);
      setError(err?.message || "Unable to load Case Analyzer status.");
      pollTimer.current = setTimeout(fetchStatus, 3500);
    }
  };

  useEffect(() => {
    if (!authReady || !guardChecked || !idToken || !jobId) return;

    if (pollTimer.current) clearTimeout(pollTimer.current);
    fetchStatus();

    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    
  }, [authReady, guardChecked, idToken, jobId]);

  const job = payload?.job || payload || {};
  const progress = job?.progress || payload?.progress || {};
  const status = job?.status || payload?.status || progress?.stage || "queued";
  const normalized = normalizeStatus(status, progress);
  const stageMeta = getStageMeta(status, progress);
  const activeIndex = statusIndex(status, progress);
  const isDone = normalized === "complete";
  const isError = normalized === "error";
  const progressPercent = clamp(pickFirst(progress?.percent, stageMeta.percent, isDone ? 100 : 5));

  const report = normalizeDashboardPayload(reportPayload);
  const dashboardArtifact = report?.dashboardArtifact || report?.dashboard_artifact || {};
  const dashboard =
    report?.dashboard ||
    report?.dashboardData ||
    report?.dashboard_data ||
    dashboardArtifact?.dashboard ||
    dashboardArtifact?.dashboardData ||
    dashboardArtifact?.dashboard_data ||
    {};
  const charts =
    report?.charts ||
    report?.chartData ||
    report?.chart_data ||
    dashboardArtifact?.charts ||
    dashboardArtifact?.chartData ||
    dashboardArtifact?.chart_data ||
    dashboard?.charts ||
    {};
  const liveView =
    report?.liveView ||
    report?.live_view ||
    dashboardArtifact?.liveView ||
    dashboardArtifact?.live_view ||
    dashboard?.liveView ||
    dashboard?.live_view ||
    firstDeepObject({ report, dashboardArtifact, dashboard }, ["liveView", "live_view"]) ||
    {};

  const dashboardSummary =
    dashboard?.dashboardSummary ||
    dashboard?.dashboard_summary ||
    report?.dashboardSummary ||
    report?.dashboard_summary ||
    dashboardArtifact?.dashboardSummary ||
    dashboardArtifact?.dashboard_summary ||
    {};

  const allDashboardSource = { reportPayload, report, dashboardArtifact, dashboard, charts, liveView, dashboardSummary };

  const caseSnapshot =
    report?.caseSnapshot ||
    report?.case_snapshot ||
    dashboardArtifact?.caseSnapshot ||
    dashboardArtifact?.case_snapshot ||
    dashboard?.caseSnapshot ||
    dashboard?.case_snapshot ||
    firstDeepObject(allDashboardSource, ["caseSnapshot", "case_snapshot"]) ||
    {};

  const artifacts = {
    ...(reportPayload?.artifactPaths || reportPayload?.artifact_paths || {}),
    ...(reportPayload?.artifacts || {}),
    ...(report?.artifactPaths || report?.artifact_paths || {}),
    ...(report?.artifacts || {}),
    ...(dashboardArtifact?.artifactPaths || dashboardArtifact?.artifact_paths || {}),
    ...(dashboardArtifact?.artifacts || {}),
    ...(job?.artifacts || {}),
  };
  const dashboardJsonPath = pickFirst(
    report?.dashboardJsonPath,
    report?.dashboard_json_path,
    artifacts?.dashboardJsonPath,
    artifacts?.dashboard_json_path,
    job?.artifacts?.dashboardJsonPath,
    job?.artifacts?.dashboard_json_path,
    dashboardArtifact?.dashboardJsonPath,
    dashboardArtifact?.dashboard_json_path,
    dashboardArtifact?.artifactPointers?.dashboardJsonPath,
    dashboardArtifact?.artifact_pointers?.dashboard_json_path
  );
  const dashboardLoadedFromArtifact = Boolean(report?.dashboardLoadedFromArtifact || report?.dashboard_loaded_from_artifact);
  const dashboardArtifactError = report?.dashboardArtifactError || report?.dashboard_artifact_error || "";

  const payloadSnapshot = job?.payload || payload?.payload || {};
  const caseProfile =
    report?.caseProfile ||
    report?.case_profile ||
    caseSnapshot?.caseProfile ||
    caseSnapshot?.case_profile ||
    payloadSnapshot?.case_profile ||
    payloadSnapshot?.caseProfile ||
    {};

  const evidencePackage =
    report?.evidencePackage ||
    report?.evidence_package ||
    dashboardArtifact?.evidencePackage ||
    dashboardArtifact?.evidence_package ||
    dashboard?.evidencePackage ||
    dashboard?.evidence_package ||
    job?.evidencePackage ||
    payloadSnapshot?.evidence_package ||
    payloadSnapshot?.evidencePackage ||
    firstDeepObject(allDashboardSource, ["evidencePackage", "evidence_package"]) ||
    {};

  /*
   * Completed evidence files must follow the same source priority as the
   * dedicated Dashboard page. The live/dashboard artifact normally contains
   * the complete display metadata, including original file names and page
   * counts. The lightweight status job files remain a final fallback only.
   */
  const files = asArray(
    liveView?.documentFindings ||
      liveView?.document_findings ||
      report?.reviewedDocuments ||
      report?.reviewed_documents ||
      dashboardArtifact?.reviewedDocuments ||
      dashboardArtifact?.reviewed_documents ||
      evidencePackage?.files ||
      evidencePackage?.reviewedDocuments ||
      evidencePackage?.reviewed_documents ||
      report?.files ||
      firstDeepArray(allDashboardSource, [
        "reviewedDocuments",
        "reviewed_documents",
        "documentFindings",
        "document_findings",
        "files",
      ]) ||
      job?.files ||
      []
  );

  const scoreDashboard = scoreDashboardObject(report, dashboard, charts, liveView, dashboardArtifact);

  const probabilityScore = clamp(
    pickFirst(
      dashboard?.probabilityScore,
      dashboard?.probability_score,
      report?.probabilityScore,
      report?.probability_score,
      getByPath(report, "dashboard.probabilityScore"),
      getByPath(report, "dashboard.probability_score"),
      getByPath(report, "probability_profile.probability_score_0_to_100"),
      getByPath(report, "probability_profile.score_0_to_100"),
      getByPath(report, "probability.score_0_to_100"),
      getByPath(report, "probability.score"),
      scoreFromScoreDashboard(scoreDashboard, ["probabilityScore", "probability_score", "Outcome posture", "Outcome Profile", "probability_score_0_to_100"]),
      firstDeepNumber(allDashboardSource, ["probabilityScore", "probability_score", "probability_score_0_to_100", "score_0_to_100"]),
      0
    )
  );

  const evidenceScore = clamp(
    pickFirst(
      dashboard?.evidenceStrength,
      dashboard?.evidence_strength,
      report?.evidenceStrength,
      report?.evidence_strength,
      scoreFromScoreDashboard(scoreDashboard, ["evidenceStrength", "evidence_strength", "Evidence Strength"]),
      getByPath(report, "score_dashboard.evidence_strength"),
      firstDeepNumber(allDashboardSource, ["evidenceStrength", "evidence_strength"]),
      0
    )
  );

  const reliabilityScore = clamp(
    pickFirst(
      dashboard?.scoreReliabilityScore,
      dashboard?.score_reliability_score,
      dashboard?.evidenceReliability,
      dashboard?.evidence_reliability,
      report?.scoreReliabilityScore,
      report?.score_reliability_score,
      report?.evidenceReliability,
      report?.evidence_reliability,
      scoreFromScoreDashboard(scoreDashboard, ["scoreReliability", "score_reliability", "scoreReliabilityScore", "score_reliability_score", "Evidence Reliability", "Score Reliability", "Reliability"]),
      getByPath(report, "score_dashboard.score_reliability"),
      getByPath(report, "score_reliability.score"),
      firstDeepNumber(allDashboardSource, ["scoreReliability", "score_reliability", "scoreReliabilityScore", "score_reliability_score", "evidenceReliability", "evidence_reliability"]),
      0
    )
  );

  const settlementScore = clamp(
    pickFirstNumeric(
      dashboard?.settlement_leverage,
      report?.settlement_leverage,
      getByPath(report, "score_dashboard.settlement_leverage"),
      scoreFromScoreDashboard(scoreDashboard, ["settlement_leverage", "Settlement Leverage"]),
      dashboard?.settlementLeverage,
      report?.settlementLeverage,
      0
    )
  );

  const probabilityBand = pickFirst(
    dashboard?.probabilityBand,
    dashboard?.probability_band,
    report?.probabilityBand,
    report?.probability_band,
    getByPath(report, "probability_profile.probability_band"),
    getByPath(report, "probability.band"),
    firstDeepValue(allDashboardSource, ["probabilityBand", "probability_band"]),
    "Pending"
  );

  const summaryText =
    liveView?.executiveSummary ||
    liveView?.executive_summary ||
    dashboard?.executiveSummary ||
    dashboard?.executive_summary ||
    dashboard?.summary ||
    report?.summary ||
    getByPath(report, "executive_summary.summary") ||
    getByPath(report, "executive_summary.case_posture_summary") ||
    firstDeepValue(allDashboardSource, ["case_posture_summary", "casePostureSummary", "downloadReadySummary", "download_ready_summary", "executiveSummary", "executive_summary", "summary"]) ||
    dashboard?.probabilityExplanation ||
    dashboard?.probability_explanation ||
    progress?.message ||
    stageMeta.text ||
    (isDone
      ? "Your Case Analyzer report is ready."
      : "Your case package is being processed. This page updates automatically.");

  const pdfUrl = pickFirst(
    artifacts?.reportPdfUrl,
    artifacts?.report_pdf_url,
    artifacts?.reportPdfSignedUrl,
    artifacts?.report_pdf_signed_url,
    artifacts?.fileVaultUrl,
    artifacts?.file_vault_url,
    job?.reportPdfUrl,
    job?.report_pdf_url,
    report?.reportPdfUrl,
    report?.report_pdf_url
  );

  const jsonUrl = pickFirst(
    artifacts?.reportJsonUrl,
    artifacts?.report_json_url,
    job?.reportJsonUrl,
    job?.report_json_url,
    report?.reportJsonUrl,
    report?.report_json_url
  );

  const dashboardJsonUrl = pickFirst(
    artifacts?.dashboardJsonUrl,
    artifacts?.dashboard_json_url,
    artifacts?.dashboardJsonSignedUrl,
    artifacts?.dashboard_json_signed_url,
    report?.dashboardJsonUrl,
    report?.dashboard_json_url,
    report?.dashboardArtifact?.dashboardJsonUrl,
    report?.dashboardArtifact?.dashboard_json_url,
    report?.dashboard_artifact?.dashboardJsonUrl,
    report?.dashboard_artifact?.dashboard_json_url,
    reportPayload?.dashboardJsonUrl,
    reportPayload?.dashboard_json_url,
    reportPayload?.artifacts?.dashboardJsonUrl,
    reportPayload?.artifacts?.dashboard_json_url,
    dashboardArtifact?.dashboardJsonUrl,
    dashboardArtifact?.dashboard_json_url,
    dashboardArtifact?.artifacts?.dashboardJsonUrl,
    dashboardArtifact?.artifacts?.dashboard_json_url,
    dashboardArtifact?.artifactPaths?.dashboardJsonUrl,
    dashboardArtifact?.artifact_paths?.dashboard_json_url,
    job?.dashboardJsonUrl,
    job?.dashboard_json_url,
    job?.artifacts?.dashboardJsonUrl,
    job?.artifacts?.dashboard_json_url
  );

  // TEMPORARY DEBUG CONTROL:
  // Exposed only while diagnosing the Stage 2 file-report -> document-summary
  // handoff. Remove this URL resolver together with the temporary button once
  // the missing-gaps issue is resolved.
  const fileReportsJsonUrl = pickFirst(
    artifacts?.fileReportsUrl,
    artifacts?.file_reports_url,
    artifacts?.fileReportsJsonUrl,
    artifacts?.file_reports_json_url,
    artifacts?.fileReportsSignedUrl,
    artifacts?.file_reports_signed_url,
    report?.fileReportsUrl,
    report?.file_reports_url,
    report?.fileReportsJsonUrl,
    report?.file_reports_json_url,
    reportPayload?.fileReportsUrl,
    reportPayload?.file_reports_url,
    reportPayload?.fileReportsJsonUrl,
    reportPayload?.file_reports_json_url,
    reportPayload?.artifacts?.fileReportsUrl,
    reportPayload?.artifacts?.file_reports_url,
    reportPayload?.artifactPaths?.fileReportsUrl,
    reportPayload?.artifact_paths?.file_reports_url,
    dashboardArtifact?.fileReportsUrl,
    dashboardArtifact?.file_reports_url,
    dashboardArtifact?.artifacts?.fileReportsUrl,
    dashboardArtifact?.artifacts?.file_reports_url,
    dashboardArtifact?.artifactPointers?.fileReportsUrl,
    dashboardArtifact?.artifact_pointers?.file_reports_url,
    job?.fileReportsUrl,
    job?.file_reports_url,
    job?.artifacts?.fileReportsUrl,
    job?.artifacts?.file_reports_url
  );

  const reportPdfPath = pickFirst(
    artifacts?.reportPdfPath,
    artifacts?.report_pdf_path,
    artifacts?.fileVaultPath,
    artifacts?.file_vault_path,
    job?.artifacts?.reportPdfPath
  );

  const reportJsonPath = pickFirst(
    artifacts?.reportJsonPath,
    artifacts?.report_json_path,
    job?.artifacts?.reportJsonPath
  );

  const fileCount = getNumber(
    pickFirst(
      report?.evidenceFileCount,
      report?.evidence_file_count,
      artifacts?.evidenceFileCount,
      artifacts?.evidence_file_count,
      evidencePackage?.uploadedFileCount,
      evidencePackage?.uploaded_file_count,
      evidencePackage?.total_file_count,
      evidencePackage?.totalFileCount,
      firstDeepNumber(allDashboardSource, ["evidenceFileCount", "evidence_file_count", "fileCount", "file_count", "totalFileCount", "total_file_count"]),
      files.length
    )
  );

  const pageCount = getNumber(
    pickFirst(
      evidencePackage?.totalPageCount,
      evidencePackage?.total_page_count,
      evidencePackage?.knownPageCount,
      evidencePackage?.known_page_count
    )
  );

  const uploadMb = getNumber(evidencePackage?.totalUploadMb || evidencePackage?.total_upload_mb);

  const caseType = pickFirst(
    caseSnapshot?.caseType,
    caseSnapshot?.case_type,
    caseProfile?.case_type,
    caseProfile?.caseType,
    "Pending"
  );

  const caseSubtype = pickFirst(
    caseSnapshot?.caseSubtype,
    caseSnapshot?.case_subtype,
    caseProfile?.case_subtype,
    caseProfile?.caseSubtype,
    "Pending"
  );

  const documentFindings = asArray(
    liveView?.documentFindings ||
      liveView?.document_findings ||
      report?.reviewedDocuments ||
      report?.reviewed_documents ||
      dashboardArtifact?.reviewedDocuments ||
      dashboardArtifact?.reviewed_documents ||
      files
  );
  const caseTypeFramework = getCaseTypeFramework(report, liveView);
  const playbookUsageSummary = getPlaybookUsage(report, liveView);
  const evidencePlaybookRows = normalizeEvidencePlaybookRows(playbookUsageSummary);
  const hasHistoryDashboard = Boolean(reportPayload && (dashboardLoadedFromArtifact || hasUsableDashboardPayload(reportPayload || report)));
  const rendererText = buildRendererText({ report, dashboard, charts, liveView, probabilityBand, dashboardArtifact });
  const safeSummaryText = cleanDisplayText(summaryText, isDone ? "Your Case Analyzer report is ready." : stageMeta.text);

  const detailedScoreItems = buildDetailedScoreVisualItems({
    scoreDashboard,
    charts,
    dashboardArtifact,
    report,
    dashboard,
    probabilityScore,
    evidenceScore,
    reliabilityScore,
    settlementScore,
  });
  const outcomeRangeItems = buildOutcomeRangeItems({ report, dashboard, dashboardArtifact });
  const riskVisualItems = buildRiskVisualItems({ charts, dashboardArtifact, report, dashboard, scoreDashboard });
  const evidenceSufficiencyItems = buildEvidenceSufficiencyItems({ charts, dashboardArtifact, report, dashboard });
  const priorityEvidenceItems = buildPriorityEvidenceItems({ charts, dashboardArtifact, dashboard, liveView, report });
  const dashboardReady = hasUsableDashboardPayload(reportPayload || report);
  const keyStrengths = compactDisplayItems(
    dashboardSummary?.top_strengths ||
      dashboardSummary?.topStrengths ||
      dashboard?.keyStrengths ||
      dashboard?.key_strengths ||
      liveView?.keyStrengths ||
      liveView?.key_strengths ||
      firstDeepArray(allDashboardSource, ["keyStrengths", "key_strengths", "topStrengths", "top_strengths"]),
    5
  );
  const keyRisks = compactDisplayItems(
    dashboardSummary?.top_risks ||
      dashboardSummary?.topRisks ||
      dashboard?.topRisks ||
      dashboard?.top_risks ||
      liveView?.topRisks ||
      liveView?.top_risks ||
      firstDeepArray(allDashboardSource, ["keyRisks", "key_risks", "topRisks", "top_risks", "keyWeaknesses", "key_weaknesses"]),
    5
  );

  const evidenceFilePreviewLimit = 6;
  const visibleEvidenceFiles = showAllEvidenceFiles ? files : files.slice(0, evidenceFilePreviewLimit);
  const hiddenEvidenceFileCount = Math.max(0, files.length - visibleEvidenceFiles.length);

  const openLoadedDashboardJson = () => {
    openJsonPayloadInNewTab(reportPayload || report, `case_analyzer_dashboard_${jobId || "job"}.json`);
  };

  const openLoadedReportJson = () => {
    openJsonPayloadInNewTab(report, `case_analyzer_report_payload_${jobId || "job"}.json`);
  };

  if (!guardChecked || !authReady) {
    return (
      <div style={styles.outerWrap}>
        <div style={styles.loadingCard}>
          <Spinner size={24} />
          <div style={styles.loadingTitle}>Preparing Case Analyzer status…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="case-status-page" style={styles.outerWrap}>
      <style>{`
        @keyframes caseAnalyzerSpin { to { transform: rotate(360deg); } }

        .case-status-page,
        .case-status-page * {
          box-sizing: border-box;
        }

        .case-status-page {
          width: 100%;
          max-width: 1180px;
          overflow-x: hidden;
        }

        .case-status-page > *,
        .case-status-page section,
        .case-status-page article,
        .case-status-page aside,
        .case-status-page header,
        .case-status-page div {
          min-width: 0;
          max-width: 100%;
        }

        .case-status-page img,
        .case-status-page svg,
        .case-status-page canvas,
        .case-status-page video {
          max-width: 100%;
          height: auto;
        }

        .case-status-page pre,
        .case-status-page code {
          max-width: 100%;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .case-status-page table {
          max-width: 100%;
        }

        .case-status-page th,
        .case-status-page td,
        .case-status-page p,
        .case-status-page li,
        .case-status-page span,
        .case-status-page button,
        .case-status-page a {
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .case-status-page .case-status-scroll-guard,
        .case-status-page .answer-renderer-table-wrap,
        .case-status-page .markdown-table-wrapper {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        @media (max-width: 1023px) {
          .case-status-main-grid { grid-template-columns: 1fr !important; }
          .case-status-stage-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
          .case-status-report-grid,
          .case-dashboard-report-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .case-status-score-grid,
          .case-dashboard-score-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .case-status-two-column-grid,
          .case-dashboard-main-grid,
          .case-dashboard-visual-grid { grid-template-columns: 1fr !important; }
          .case-status-action-row,
          .case-dashboard-action-row {
            flex-wrap: wrap !important;
          }
          .case-status-page {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
        }

        @media (max-width: 700px) {
          .case-status-stage-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .case-status-report-grid,
          .case-dashboard-report-grid,
          .case-status-score-grid,
          .case-dashboard-score-grid { grid-template-columns: 1fr !important; }
          .case-status-action-row,
          .case-dashboard-action-row {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .case-status-action-row > *,
          .case-dashboard-action-row > * {
            width: 100% !important;
            max-width: 100% !important;
          }
          .case-status-progress-top {
            display: block !important;
          }
          .case-status-job-badge {
            margin-top: 10px !important;
            max-width: 100% !important;
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: break-all !important;
          }
          .case-status-segment-labels {
            gap: 4px !important;
            font-size: 10px !important;
          }
        }

        @media (max-width: 620px) {
          .case-status-hero {
            display: block !important;
            text-align: center !important;
          }
          .case-status-status-pill {
            margin-top: 12px !important;
            justify-content: center !important;
            width: 100% !important;
          }
          .case-status-processing-note-head {
            align-items: flex-start !important;
          }
          .case-status-processing-note-toggle {
            flex: 0 0 auto !important;
          }
        }

        @media (max-width: 420px) {
          .case-status-page {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
          .case-status-stage-grid { grid-template-columns: 1fr !important; }
          .case-status-segment-labels {
            display: grid !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            text-align: center !important;
            row-gap: 5px !important;
          }
        }


        @keyframes caseAnalyzerSpin { to { transform: rotate(360deg); } }

        html, body, #root {
          max-width: 100%;
          overflow-x: hidden;
        }

        .case-dashboard-page,
        .case-dashboard-page *,
        .case-dashboard-main-grid,
        .case-dashboard-main-grid > *,
        .case-dashboard-renderer-shell,
        .case-dashboard-renderer-canvas,
        .case-dashboard-visual-grid,
        .case-dashboard-mobile-insights {
          box-sizing: border-box;
          min-width: 0;
          max-width: 100%;
        }

        .case-dashboard-page {
          width: 100%;
          overflow-x: hidden;
          contain: inline-size;
        }

        /*
         * The Dashboard must not own one shared horizontal scrollbar for the
         * entire AnswerRenderer. Each chart and each table owns its own local
         * horizontal scrolling inside AnswerChart and AnswerTable.
         */
        .case-dashboard-renderer-shell {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow: visible;
        }

        .case-dashboard-renderer-canvas {
          display: block;
          width: 100%;
          min-width: 0;
          max-width: 100%;
          background: #F8FAFC;
          border-radius: 14px;
          vertical-align: top;
          overflow: visible;
        }

        .case-dashboard-renderer-canvas > * {
          width: 100%;
          min-width: 0;
          max-width: 100% !important;
          background: inherit;
        }

        .case-dashboard-renderer-canvas .answer-table-region,
        .case-dashboard-renderer-canvas .answer-table-scroll {
          width: 100%;
          min-width: 0;
          max-width: 100%;
        }

        .case-dashboard-renderer-canvas .answer-table-region {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          overflow: visible;
        }

        /*
         * FORCE ONE INDEPENDENT HORIZONTAL SCROLLBAR PER TABLE.
         *
         * The Dashboard-wide renderer does not scroll. Every AnswerTable
         * instance owns its own horizontal scrollbar, including Timeline
         * Highlights, Missing Evidence, and Evidence Bundle Comparison.
         *
         * overflow-x: scroll intentionally keeps the scrollbar available on
         * desktop, laptop, tablet, and mobile even when the browser believes
         * the table nearly fits.
         */
        .case-dashboard-renderer-canvas .answer-table-scroll,
        .case-dashboard-renderer-canvas .answer-table-scroll-fits,
        .case-dashboard-renderer-canvas .answer-table-scroll-has-overflow {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          overflow-x: scroll !important;
          overflow-y: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          overscroll-behavior-x: contain !important;
          scroll-behavior: smooth !important;
          scrollbar-gutter: stable both-edges !important;
          touch-action: pan-x pan-y !important;
          padding-bottom: 8px !important;
          position: relative !important;
          background: #FFFFFF !important;
        }

        /*
         * The global Dashboard overflow guard sets max-width:100% on all
         * descendants. These stronger rules release only the table content,
         * allowing the table to become wider than its viewport so local
         * horizontal scrolling actually works.
         */
        .case-dashboard-renderer-canvas .answer-table-wrap {
          display: block !important;
          width: max-content !important;
          min-width: 100% !important;
          max-width: none !important;
          overflow: visible !important;
          background: #FFFFFF !important;
        }

        .case-dashboard-renderer-canvas .answer-table {
          width: max-content !important;
          min-width: 100% !important;
          max-width: none !important;
          table-layout: auto !important;
        }

        .case-dashboard-renderer-canvas .answer-table th,
        .case-dashboard-renderer-canvas .answer-table td {
          max-width: none;
        }

        /*
         * FINAL LAST-COLUMN EDGE PROTECTION
         *
         * The last column keeps an internal safety zone so long source/file
         * references never visually touch or clip against the table's own
         * right border. The cell receives 8px of right padding and the text
         * span receives an additional 3px internal buffer.
         *
         * Wrapping stays conservative with break-word, so a row becomes taller
         * only when the content truly cannot fit on one line.
         */
        .case-dashboard-renderer-canvas .answer-table th:last-child,
        .case-dashboard-renderer-canvas .answer-table td:last-child {
          padding-right: 8px !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: break-word !important;
          hyphens: auto;
        }

        .case-dashboard-renderer-canvas
          .answer-table th:last-child
          .answer-table-cell-content,
        .case-dashboard-renderer-canvas
          .answer-table td:last-child
          .answer-table-cell-content {
          display: block !important;
          width: auto !important;
          max-width: 100% !important;
          padding-right: 3px !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: break-word !important;
          hyphens: auto;
        }

        /*
         * Make each table scrollbar more visible. Firefox uses
         * scrollbar-width/color; Chromium/Safari use WebKit selectors.
         */
        .case-dashboard-renderer-canvas .answer-table-scroll {
          scrollbar-width: auto !important;
          scrollbar-color: rgba(30, 41, 59, 0.72) rgba(148, 163, 184, 0.22) !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar {
          height: 14px !important;
          display: block !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar-track {
          background: rgba(148, 163, 184, 0.22) !important;
          border-radius: 999px !important;
          border: 2px solid #FFFFFF !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar-thumb {
          min-width: 48px !important;
          background: rgba(30, 41, 59, 0.72) !important;
          border-radius: 999px !important;
          border: 3px solid #FFFFFF !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(15, 23, 42, 0.9) !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll-instruction {
          display: block !important;
          width: 100% !important;
          padding: 6px 8px 0 !important;
          color: #64748B !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          line-height: 1.35 !important;
          text-align: right !important;
        }

        /*
         * LEGEND-SAFE CHART CONTAINMENT
         *
         * Keep the chart card and preview width-safe without clipping the
         * Recharts legend. The legend is allowed to wrap naturally inside the
         * card, while the chart preview keeps its own local horizontal scroll.
         */
        .case-dashboard-renderer-canvas .answer-chart-card {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          position: relative !important;
          overflow: visible !important;
          box-sizing: border-box !important;
        }

        .case-dashboard-renderer-canvas .answer-chart-preview {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          padding-left: 10px;
          padding-right: 10px;
          scroll-padding-left: 10px;
          scroll-padding-right: 10px;
          overflow-x: auto !important;
          overflow-y: visible !important;
          box-sizing: border-box !important;
        }

        .case-dashboard-renderer-canvas .recharts-wrapper {
          max-width: 100% !important;
          overflow: visible !important;
        }

        .case-dashboard-renderer-canvas .answer-chart-canvas {
          min-width: calc(100% - 20px);
          padding-left: 6px;
          padding-right: 6px;
        }

        .case-dashboard-renderer-canvas svg text {
          font-size: 12.5px !important;
          font-weight: 750 !important;
        }

        .case-dashboard-renderer-canvas .recharts-legend-wrapper {
          width: 100% !important;
          max-width: 100% !important;
          left: 0 !important;
          right: 0 !important;
          margin: 0 auto !important;
          box-sizing: border-box !important;
          white-space: normal !important;
          overflow: visible !important;
          overflow-wrap: normal !important;
          word-break: normal !important;
          font-size: 13px !important;
          line-height: 1.35 !important;
          padding-left: 12px !important;
          padding-right: 12px !important;
        }

        .case-dashboard-renderer-canvas .recharts-default-legend {
          display: flex !important;
          flex-wrap: wrap !important;
          justify-content: center !important;
          align-items: flex-start !important;
          gap: 8px 14px !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 auto !important;
          padding: 0 !important;
          box-sizing: border-box !important;
        }

        .case-dashboard-renderer-canvas .recharts-legend-item {
          display: inline-flex !important;
          align-items: flex-start !important;
          flex: 0 1 calc(50% - 14px) !important;
          min-width: 0 !important;
          max-width: calc(50% - 14px) !important;
          margin-right: 0 !important;
          white-space: normal !important;
          box-sizing: border-box !important;
        }

        .case-dashboard-renderer-canvas .recharts-legend-item-text {
          display: inline-block !important;
          min-width: 0 !important;
          max-width: 100% !important;
          white-space: normal !important;
          overflow-wrap: normal !important;
          word-break: normal !important;
          hyphens: none !important;
          font-size: 13px !important;
          line-height: 1.35 !important;
        }

        .case-dashboard-renderer-canvas .recharts-tooltip-wrapper {
          max-width: min(260px, calc(100vw - 48px)) !important;
        }

        .case-dashboard-score-scroll {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }

        .case-dashboard-score-canvas {
          display: inline-block;
          width: max-content;
          min-width: 100%;
          max-width: none;
          background: #FFFFFF;
          border-radius: 12px;
          vertical-align: top;
        }

        .case-dashboard-score-canvas > * {
          min-width: 100%;
          width: max-content;
          max-width: none;
        }

        .case-dashboard-mobile-insights {
          display: none;
        }

        @media (max-width: 1023px) {
          .case-dashboard-page {
            width: 100% !important;
            max-width: 100% !important;
            padding: 22px 12px 34px !important;
            overflow-x: hidden !important;
          }

          .case-dashboard-hero {
            display: block !important;
            text-align: center !important;
          }

          .case-dashboard-title {
            font-size: 28px !important;
            line-height: 1.18 !important;
            text-align: center !important;
            overflow-wrap: anywhere !important;
          }

          .case-dashboard-subtitle,
          .case-dashboard-completed-line {
            margin-left: auto !important;
            margin-right: auto !important;
            text-align: center !important;
            overflow-wrap: anywhere !important;
          }

          .case-dashboard-new-button {
            margin: 18px auto 0 !important;
            max-width: 100% !important;
            justify-content: center !important;
          }

          .case-dashboard-score-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 12px !important;
          }

          .case-dashboard-report-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }

          .case-dashboard-main-grid {
            grid-template-columns: minmax(0, 1fr) !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .case-dashboard-main-grid > * {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          .case-dashboard-mobile-insights {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 14px !important;
            margin-bottom: 18px !important;
          }

          .case-dashboard-action-row {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 10px !important;
          }

          .case-dashboard-action-row a,
          .case-dashboard-action-row button {
            width: 100% !important;
            max-width: 100% !important;
            white-space: normal !important;
            text-align: center !important;
          }

          .case-dashboard-visual-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .case-dashboard-panel-header {
            display: flex !important;
            flex-wrap: nowrap !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 10px !important;
          }

          .case-dashboard-panel-header > div {
            min-width: 0 !important;
          }

          .case-dashboard-small-pill {
            flex: 0 0 auto !important;
            white-space: nowrap !important;
            max-width: 46% !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          .case-dashboard-renderer-shell {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            padding: 12px !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas {
            display: block !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas > * {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
          }

          /*
           * Mobile/tablet table scrolling remains local to each table.
           * Do not allow the page or renderer shell to capture the gesture.
           */
          .case-dashboard-renderer-canvas .answer-table-region {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas .answer-table-scroll,
          .case-dashboard-renderer-canvas .answer-table-scroll-fits,
          .case-dashboard-renderer-canvas .answer-table-scroll-has-overflow {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow-x: scroll !important;
            overflow-y: hidden !important;
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior-x: contain !important;
            touch-action: pan-x pan-y !important;
            scrollbar-gutter: stable both-edges !important;
            padding-bottom: 10px !important;
          }

          .case-dashboard-renderer-canvas .answer-table-wrap {
            display: block !important;
            width: max-content !important;
            min-width: 100% !important;
            max-width: none !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas .answer-table {
            width: max-content !important;
            min-width: 680px !important;
            max-width: none !important;
            table-layout: auto !important;
          }

          .case-dashboard-renderer-canvas .answer-table th:last-child,
          .case-dashboard-renderer-canvas .answer-table td:last-child {
            padding-right: 8px !important;
            white-space: normal !important;
            word-break: normal !important;
            overflow-wrap: break-word !important;
          }

          .case-dashboard-renderer-canvas
            .answer-table th:last-child
            .answer-table-cell-content,
          .case-dashboard-renderer-canvas
            .answer-table td:last-child
            .answer-table-cell-content {
            max-width: 100% !important;
            padding-right: 3px !important;
            white-space: normal !important;
            word-break: normal !important;
            overflow-wrap: break-word !important;
          }

          .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar {
            height: 14px !important;
            display: block !important;
          }

          .case-dashboard-renderer-canvas .answer-table-scroll-instruction {
            display: block !important;
            padding-top: 6px !important;
            padding-bottom: 1px !important;
            font-size: 11px !important;
            text-align: right !important;
          }

          .case-dashboard-renderer-canvas .answer-chart-preview {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            padding-left: 10px !important;
            padding-right: 10px !important;
            scroll-padding-left: 10px !important;
            scroll-padding-right: 10px !important;
            overflow-x: auto !important;
            overflow-y: visible !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            left: 0 !important;
            right: 0 !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-item {
            flex: 0 1 100% !important;
            max-width: 100% !important;
          }

          .case-dashboard-renderer-canvas .answer-chart-canvas {
            min-width: calc(100% - 16px) !important;
            padding-left: 4px !important;
            padding-right: 4px !important;
          }

          .case-dashboard-renderer-canvas svg text {
            font-size: 13px !important;
            font-weight: 800 !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-wrapper {
            width: calc(100% - 20px) !important;
            max-width: calc(100% - 20px) !important;
            left: 10px !important;
            right: 10px !important;
            padding-left: 10px !important;
            padding-right: 10px !important;
            white-space: normal !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas .recharts-default-legend {
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 10px !important;
            white-space: normal !important;
            flex-wrap: wrap !important;
            justify-content: flex-start !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-item {
            max-width: 200px !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-item-text {
            max-width: 170px !important;
            font-size: 12px !important;
          }

          .case-dashboard-score-scroll {
            width: 100% !important;
            max-width: 100% !important;
          }

          .case-dashboard-score-canvas {
            min-width: 100% !important;
            width: max-content !important;
            max-width: none !important;
          }

          .case-dashboard-side-panel,
          .case-dashboard-important-note {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          .case-dashboard-file-row {
            align-items: flex-start !important;
          }

          .case-dashboard-file-name,
          .case-dashboard-file-meta {
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
          }
        }
      


        /* User-facing Status heading sizes */
        .case-status-title {
          font-size: 30px !important;
          line-height: 1.08 !important;
        }

        /* Completed dashboard on the Status page follows the final Dashboard page. */
        .case-dashboard-inline {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: hidden;
          contain: inline-size;
        }

        @media (max-width: 1023px) {
          .case-status-title {
            font-size: 28px !important;
            line-height: 1.18 !important;
          }
        }
      

        /* Synced completed-dashboard responsiveness from CaseAnalyzerDashboard.jsx */

        @keyframes caseAnalyzerSpin { to { transform: rotate(360deg); } }

        html, body, #root {
          max-width: 100%;
          overflow-x: hidden;
        }

        .case-dashboard-inline,
        .case-dashboard-inline *,
        .case-dashboard-main-grid,
        .case-dashboard-main-grid > *,
        .case-dashboard-renderer-shell,
        .case-dashboard-renderer-canvas,
        .case-dashboard-visual-grid,
        .case-dashboard-mobile-insights {
          box-sizing: border-box;
          min-width: 0;
          max-width: 100%;
        }

        .case-dashboard-inline {
          width: 100%;
          overflow-x: hidden;
          contain: inline-size;
        }

        /*
         * The Dashboard must not own one shared horizontal scrollbar for the
         * entire AnswerRenderer. Each chart and each table owns its own local
         * horizontal scrolling inside AnswerChart and AnswerTable.
         */
        .case-dashboard-renderer-shell {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow: visible;
        }

        .case-dashboard-renderer-canvas {
          display: block;
          width: 100%;
          min-width: 0;
          max-width: 100%;
          background: #F8FAFC;
          border-radius: 14px;
          vertical-align: top;
          overflow: visible;
        }

        .case-dashboard-renderer-canvas > * {
          width: 100%;
          min-width: 0;
          max-width: 100% !important;
          background: inherit;
        }

        .case-dashboard-renderer-canvas .answer-table-region,
        .case-dashboard-renderer-canvas .answer-table-scroll {
          width: 100%;
          min-width: 0;
          max-width: 100%;
        }

        .case-dashboard-renderer-canvas .answer-table-region {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          overflow: visible;
        }

        /*
         * FORCE ONE INDEPENDENT HORIZONTAL SCROLLBAR PER TABLE.
         *
         * The Dashboard-wide renderer does not scroll. Every AnswerTable
         * instance owns its own horizontal scrollbar, including Timeline
         * Highlights, Missing Evidence, and Evidence Bundle Comparison.
         *
         * overflow-x: scroll intentionally keeps the scrollbar available on
         * desktop, laptop, tablet, and mobile even when the browser believes
         * the table nearly fits.
         */
        .case-dashboard-renderer-canvas .answer-table-scroll,
        .case-dashboard-renderer-canvas .answer-table-scroll-fits,
        .case-dashboard-renderer-canvas .answer-table-scroll-has-overflow {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          overflow-x: scroll !important;
          overflow-y: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          overscroll-behavior-x: contain !important;
          scroll-behavior: smooth !important;
          scrollbar-gutter: stable both-edges !important;
          touch-action: pan-x pan-y !important;
          padding-bottom: 8px !important;
          position: relative !important;
          background: #FFFFFF !important;
        }

        /*
         * The global Dashboard overflow guard sets max-width:100% on all
         * descendants. These stronger rules release only the table content,
         * allowing the table to become wider than its viewport so local
         * horizontal scrolling actually works.
         */
        .case-dashboard-renderer-canvas .answer-table-wrap {
          display: block !important;
          width: max-content !important;
          min-width: 100% !important;
          max-width: none !important;
          overflow: visible !important;
          background: #FFFFFF !important;
        }

        .case-dashboard-renderer-canvas .answer-table {
          width: max-content !important;
          min-width: 100% !important;
          max-width: none !important;
          table-layout: auto !important;
        }

        .case-dashboard-renderer-canvas .answer-table th,
        .case-dashboard-renderer-canvas .answer-table td {
          max-width: none;
        }

        /*
         * FINAL LAST-COLUMN EDGE PROTECTION
         *
         * The last column keeps an internal safety zone so long source/file
         * references never visually touch or clip against the table's own
         * right border. The cell receives 8px of right padding and the text
         * span receives an additional 3px internal buffer.
         *
         * Wrapping stays conservative with break-word, so a row becomes taller
         * only when the content truly cannot fit on one line.
         */
        .case-dashboard-renderer-canvas .answer-table th:last-child,
        .case-dashboard-renderer-canvas .answer-table td:last-child {
          padding-right: 8px !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: break-word !important;
          hyphens: auto;
        }

        .case-dashboard-renderer-canvas
          .answer-table th:last-child
          .answer-table-cell-content,
        .case-dashboard-renderer-canvas
          .answer-table td:last-child
          .answer-table-cell-content {
          display: block !important;
          width: auto !important;
          max-width: 100% !important;
          padding-right: 3px !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: break-word !important;
          hyphens: auto;
        }

        /*
         * Make each table scrollbar more visible. Firefox uses
         * scrollbar-width/color; Chromium/Safari use WebKit selectors.
         */
        .case-dashboard-renderer-canvas .answer-table-scroll {
          scrollbar-width: auto !important;
          scrollbar-color: rgba(30, 41, 59, 0.72) rgba(148, 163, 184, 0.22) !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar {
          height: 14px !important;
          display: block !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar-track {
          background: rgba(148, 163, 184, 0.22) !important;
          border-radius: 999px !important;
          border: 2px solid #FFFFFF !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar-thumb {
          min-width: 48px !important;
          background: rgba(30, 41, 59, 0.72) !important;
          border-radius: 999px !important;
          border: 3px solid #FFFFFF !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(15, 23, 42, 0.9) !important;
        }

        .case-dashboard-renderer-canvas .answer-table-scroll-instruction {
          display: block !important;
          width: 100% !important;
          padding: 6px 8px 0 !important;
          color: #64748B !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          line-height: 1.35 !important;
          text-align: right !important;
        }

        .case-dashboard-renderer-canvas .answer-chart-card {
          width: 100%;
          min-width: 0;
          max-width: 100%;
        }

        .case-dashboard-renderer-canvas .answer-chart-preview {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          padding-left: 10px;
          padding-right: 10px;
          scroll-padding-left: 10px;
          scroll-padding-right: 10px;
        }

        .case-dashboard-renderer-canvas .answer-chart-canvas {
          min-width: calc(100% - 20px);
          padding-left: 6px;
          padding-right: 6px;
        }

        .case-dashboard-renderer-canvas svg text {
          font-size: 12.5px !important;
          font-weight: 750 !important;
        }

        /*
         * FINAL LEGEND WRAPPING
         *
         * The legend uses the full chart width with no forced left offset.
         * Items wrap naturally at spaces, and chart labels include a soft
         * break opportunity after "/" without breaking words themselves.
         */
        .case-dashboard-renderer-canvas .recharts-legend-wrapper {
          width: 100% !important;
          max-width: 100% !important;
          left: 0 !important;
          right: 0 !important;
          margin: 0 auto !important;
          box-sizing: border-box !important;
          white-space: normal !important;
          overflow: visible !important;
          overflow-wrap: normal !important;
          word-break: normal !important;
          font-size: 13px !important;
          line-height: 1.35 !important;
          padding-left: 12px !important;
          padding-right: 12px !important;
        }

        .case-dashboard-renderer-canvas .recharts-default-legend {
          display: flex !important;
          flex-wrap: wrap !important;
          justify-content: center !important;
          align-items: flex-start !important;
          gap: 8px 14px !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 auto !important;
          padding: 0 !important;
          box-sizing: border-box !important;
        }

        .case-dashboard-renderer-canvas .recharts-legend-item {
          display: inline-flex !important;
          align-items: flex-start !important;
          flex: 0 1 calc(50% - 14px) !important;
          min-width: 0 !important;
          max-width: calc(50% - 14px) !important;
          margin-right: 0 !important;
          white-space: normal !important;
          box-sizing: border-box !important;
        }

        .case-dashboard-renderer-canvas .recharts-legend-item-text {
          display: inline-block !important;
          min-width: 0 !important;
          max-width: 100% !important;
          white-space: normal !important;
          overflow-wrap: normal !important;
          word-break: normal !important;
          hyphens: none !important;
          font-size: 13px !important;
          line-height: 1.35 !important;
        }

        .case-dashboard-renderer-canvas .recharts-tooltip-wrapper {
          max-width: min(260px, calc(100vw - 48px)) !important;
        }

        .case-dashboard-score-scroll {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }

        .case-dashboard-score-canvas {
          display: inline-block;
          width: max-content;
          min-width: 100%;
          max-width: none;
          background: #FFFFFF;
          border-radius: 12px;
          vertical-align: top;
        }

        .case-dashboard-score-canvas > * {
          min-width: 100%;
          width: max-content;
          max-width: none;
        }

        .case-dashboard-mobile-insights {
          display: none;
        }

        @media (max-width: 1023px) {
          .case-dashboard-inline {
            width: 100% !important;
            max-width: 100% !important;
            padding: 22px 12px 34px !important;
            overflow-x: hidden !important;
          }

          .case-dashboard-hero {
            display: block !important;
            text-align: center !important;
          }

          .case-dashboard-title {
            font-size: 28px !important;
            line-height: 1.18 !important;
            text-align: center !important;
            overflow-wrap: anywhere !important;
          }

          .case-dashboard-subtitle,
          .case-dashboard-completed-line {
            margin-left: auto !important;
            margin-right: auto !important;
            text-align: center !important;
            overflow-wrap: anywhere !important;
          }

          .case-dashboard-new-button {
            margin: 18px auto 0 !important;
            max-width: 100% !important;
            justify-content: center !important;
          }

          .case-dashboard-score-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 12px !important;
          }

          .case-dashboard-report-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }

          .case-dashboard-main-grid {
            grid-template-columns: minmax(0, 1fr) !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          .case-dashboard-main-grid > * {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          .case-dashboard-mobile-insights {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 14px !important;
            margin-bottom: 18px !important;
          }

          .case-dashboard-action-row {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 10px !important;
          }

          .case-dashboard-action-row a,
          .case-dashboard-action-row button {
            width: 100% !important;
            max-width: 100% !important;
            white-space: normal !important;
            text-align: center !important;
          }

          .case-dashboard-visual-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .case-dashboard-panel-header {
            display: flex !important;
            flex-wrap: nowrap !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 10px !important;
          }

          .case-dashboard-panel-header > div {
            min-width: 0 !important;
          }

          .case-dashboard-small-pill {
            flex: 0 0 auto !important;
            white-space: nowrap !important;
            max-width: 46% !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          .case-dashboard-renderer-shell {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            padding: 12px !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas {
            display: block !important;
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas > * {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
          }

          /*
           * Mobile/tablet table scrolling remains local to each table.
           * Do not allow the page or renderer shell to capture the gesture.
           */
          .case-dashboard-renderer-canvas .answer-table-region {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas .answer-table-scroll,
          .case-dashboard-renderer-canvas .answer-table-scroll-fits,
          .case-dashboard-renderer-canvas .answer-table-scroll-has-overflow {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow-x: scroll !important;
            overflow-y: hidden !important;
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior-x: contain !important;
            touch-action: pan-x pan-y !important;
            scrollbar-gutter: stable both-edges !important;
            padding-bottom: 10px !important;
          }

          .case-dashboard-renderer-canvas .answer-table-wrap {
            display: block !important;
            width: max-content !important;
            min-width: 100% !important;
            max-width: none !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas .answer-table {
            width: max-content !important;
            min-width: 680px !important;
            max-width: none !important;
            table-layout: auto !important;
          }

          .case-dashboard-renderer-canvas .answer-table th:last-child,
          .case-dashboard-renderer-canvas .answer-table td:last-child {
            padding-right: 8px !important;
            white-space: normal !important;
            word-break: normal !important;
            overflow-wrap: break-word !important;
          }

          .case-dashboard-renderer-canvas
            .answer-table th:last-child
            .answer-table-cell-content,
          .case-dashboard-renderer-canvas
            .answer-table td:last-child
            .answer-table-cell-content {
            max-width: 100% !important;
            padding-right: 3px !important;
            white-space: normal !important;
            word-break: normal !important;
            overflow-wrap: break-word !important;
          }

          .case-dashboard-renderer-canvas .answer-table-scroll::-webkit-scrollbar {
            height: 14px !important;
            display: block !important;
          }

          .case-dashboard-renderer-canvas .answer-table-scroll-instruction {
            display: block !important;
            padding-top: 6px !important;
            padding-bottom: 1px !important;
            font-size: 11px !important;
            text-align: right !important;
          }

          .case-dashboard-renderer-canvas .answer-chart-preview {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
            scroll-padding-left: 8px !important;
            scroll-padding-right: 8px !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            left: 0 !important;
            right: 0 !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-item {
            flex: 0 1 100% !important;
            max-width: 100% !important;
          }

          .case-dashboard-renderer-canvas .answer-chart-canvas {
            min-width: calc(100% - 16px) !important;
            padding-left: 4px !important;
            padding-right: 4px !important;
          }

          .case-dashboard-renderer-canvas svg text {
            font-size: 13px !important;
            font-weight: 800 !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-wrapper {
            width: calc(100% - 20px) !important;
            max-width: calc(100% - 20px) !important;
            left: 10px !important;
            right: 10px !important;
            padding-left: 10px !important;
            padding-right: 10px !important;
            white-space: normal !important;
            overflow: visible !important;
          }

          .case-dashboard-renderer-canvas .recharts-default-legend {
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 10px !important;
            white-space: normal !important;
            flex-wrap: wrap !important;
            justify-content: flex-start !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-item {
            max-width: 200px !important;
          }

          .case-dashboard-renderer-canvas .recharts-legend-item-text {
            max-width: 170px !important;
            font-size: 12px !important;
          }

          .case-dashboard-score-scroll {
            width: 100% !important;
            max-width: 100% !important;
          }

          .case-dashboard-score-canvas {
            min-width: 100% !important;
            width: max-content !important;
            max-width: none !important;
          }

          .case-dashboard-side-panel,
          .case-dashboard-important-note {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          .case-dashboard-file-row {
            align-items: flex-start !important;
          }

          .case-dashboard-file-name,
          .case-dashboard-file-meta {
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
          }
        }
      
        @media (max-width: 1023px) {
          .case-dashboard-inline {
            padding: 0 !important;
          }
        }
`}</style>

      <header className="case-status-hero" style={styles.hero}>
        <div>
          <div style={styles.kicker}>Case Analyzer</div>
          <h1 className="case-status-title" style={styles.title}>Case analysis status</h1>
          <p style={styles.subtitle}>
            Track your case analysis progress and open the completed report when it is ready.
          </p>
        </div>

        <div
          className="case-status-status-pill"
          style={{
            ...styles.statusPill,
            borderColor: `${stageMeta.color}33`,
            background: isDone ? "#ECFDF5" : isError ? "#FEF2F2" : "#FFFFFF",
            color: stageMeta.color,
          }}
        >
          {!isDone && !isError ? <Spinner size={15} color={stageMeta.color} /> : <span style={{ ...styles.statusDot, background: stageMeta.color }} />}
          <span>{stageMeta.label}</span>
        </div>
      </header>

      {error && (
        <div style={styles.errorBox}>
          <strong>Notice:</strong> {error}
        </div>
      )}

      <section style={styles.progressPanel}>
        <div className="case-status-progress-top" style={styles.progressTop}>
          <div style={styles.progressCopy}>
            <h2 style={styles.progressTitle}>Analysis progress</h2>
            <p style={styles.sectionText}>{progress?.message || stageMeta.text}</p>
          </div>
          <div className="case-status-job-badge" style={styles.jobBadge}>{jobId}</div>
        </div>

        {!isDone && !isError ? (
          <div style={styles.processingNote}>
            <div className="case-status-processing-note-head" style={styles.processingNoteHead}>
              <div style={styles.processingNoteLead}>
                <span style={styles.processingNoteIcon}>✓</span>
                <strong style={styles.processingNoteTitle}>Your analysis is continuing in the background.</strong>
              </div>
              <button
                type="button"
                className="case-status-processing-note-toggle"
                onClick={() => setShowProcessingDetails((value) => !value)}
                style={styles.processingNoteToggle}
                aria-expanded={showProcessingDetails}
              >
                {showProcessingDetails ? "Hide details" : "Show details"}
              </button>
            </div>

            {showProcessingDetails ? (
              <p style={styles.processingNoteText}>
                You may safely leave this page or close your browser and return anytime through Analysis History. Because your files are reviewed through multiple levels of document analysis, evidence comparison, case-level review, and quality checks—not a lightweight scan—larger or more complex cases may take additional time.
              </p>
            ) : null}
          </div>
        ) : null}

        <div style={styles.instrumentPanel}>
          <div style={styles.instrumentTop}>
            <div>
              <div style={styles.instrumentLabel}>Overall progress</div>
              <div style={styles.instrumentSub}>{loading ? "Loading job status…" : `${Math.round(progressPercent)}% complete`}</div>
            </div>
            <div style={{ ...styles.instrumentPercent, color: stageMeta.color }}>{Math.round(progressPercent)}%</div>
          </div>
          <div style={styles.segmentTrack}>
            <div
              style={{
                ...styles.segmentFill,
                width: `${progressPercent}%`,
                background: `linear-gradient(90deg, #2563EB, ${stageMeta.color})`,
              }}
            />
          </div>
          <div className="case-status-segment-labels" style={styles.segmentLabels}>
            <span>Received</span>
            <span>Reading</span>
            <span>Summarizing</span>
            <span>Analyzing</span>
            <span>Ready</span>
          </div>
        </div>

        <div className="case-status-stage-grid" style={styles.stageGrid}>
          {STATUS_STEPS.map((step, idx) => {
            const done = isDone || activeIndex > idx;
            const current = activeIndex === idx && !isDone && !isError;
            const pending = !done && !current;
            return (
              <div
                key={step.key}
                style={{
                  ...styles.stageCard,
                  ...(done ? styles.stageDone : null),
                  ...(current ? styles.stageCurrent : null),
                  ...(pending ? styles.stagePending : null),
                  borderColor: done || current ? `${step.color}44` : "rgba(15,23,42,0.08)",
                }}
              >
                <div style={styles.stageTop}>
                  <span style={{ ...styles.stageIcon, background: done || current ? step.color : "#E2E8F0", color: done || current ? "#FFFFFF" : "#64748B" }}>
                    {done ? "✓" : idx + 1}
                  </span>
                  {current ? <Spinner size={13} color={step.color} /> : null}
                </div>
                <div style={styles.stageLabel}>{step.shortLabel || step.label}</div>
              </div>
            );
          })}
        </div>
      </section>


      {isDone ? (
        <div className="case-dashboard-inline" style={styles.completedDashboardWrap}>
          <section className="case-dashboard-score-grid" style={styles.scoreGrid}>
            <ScoreTile title="Outcome posture" value={probabilityScore} label="Probability profile" color="#2563EB" />
            <ScoreTile title="Evidence strength" value={evidenceScore} label="Evidence support" color="#059669" variant="bar" />
            <ScoreTile title="Reliability" value={reliabilityScore} label="Source confidence" color="#7C3AED" />
            <ScoreTile title="Settlement leverage" value={settlementScore} label="Negotiation posture" color="#EA580C" variant="bar" />
          </section>

          {!hasHistoryDashboard ? (
            <div style={styles.pendingHistoryBox}>
              The job is complete. The full dashboard is still loading. This page will keep checking automatically; no manual refresh is needed.
            </div>
          ) : null}

          {dashboardArtifactError ? (
            <div style={styles.pendingHistoryBox}>
              Dashboard information is saved, but it could not be loaded yet: {cleanDisplayText(dashboardArtifactError)}
            </div>
          ) : null}

          <div className="case-dashboard-main-grid" style={styles.mainGrid}>
            <main style={styles.mainColumn}>
              <section style={styles.reportPanel}>
                <div className="case-dashboard-panel-header" style={styles.panelHeader}>
                  <div>
                    <h2 style={styles.sectionTitle}>Dashboard summary</h2>
                  </div>
                  <span className="case-dashboard-small-pill" style={styles.smallPill}>
                    {probabilityBand || "Pending"}
                  </span>
                </div>

                <p style={styles.summaryText}>{safeSummaryText}</p>

                <div className="case-dashboard-report-grid" style={styles.reportGrid}>
                  <MetricCard label="Case type" value={caseType} />
                  <MetricCard label="Subtype" value={caseSubtype} />
                  <MetricCard label="Probability band" value={probabilityBand || "Pending"} />
                  <MetricCard label="Evidence files" value={fileCount || "—"} />
                </div>

                <div className="case-dashboard-action-row" style={styles.actionRow}>
                  {pdfUrl && isSignedOrHttpUrl(pdfUrl) ? (
                    <a href={pdfUrl} target="_blank" rel="noreferrer" style={styles.primaryButton}>
                      Open full PDF report
                    </a>
                  ) : reportPdfPath ? (
                    <Link to="/file-vault" style={styles.primaryButton}>
                      Open report in File Vault
                    </Link>
                  ) : (
                    <button type="button" style={{ ...styles.primaryButton, ...styles.disabledButton }} disabled>
                      PDF report pending
                    </button>
                  )}

                  {jsonUrl ? (
                    <a href={jsonUrl} target="_blank" rel="noreferrer" style={styles.secondaryButton}>
                      Open Report JSON
                    </a>
                  ) : report ? (
                    <button type="button" onClick={openLoadedReportJson} style={styles.secondaryButton}>
                      Open Loaded Report JSON
                    </button>
                  ) : null}

                  {dashboardJsonUrl && isSignedOrHttpUrl(dashboardJsonUrl) ? (
                    <a href={dashboardJsonUrl} target="_blank" rel="noreferrer" style={styles.secondaryButton}>
                      Open Dashboard JSON
                    </a>
                  ) : reportPayload ? (
                    <button type="button" onClick={openLoadedDashboardJson} style={styles.secondaryButton}>
                      Open Dashboard JSON
                    </button>
                  ) : null}

                  {/* TEMPORARY: third JSON diagnostic button. */}
                  {fileReportsJsonUrl && isSignedOrHttpUrl(fileReportsJsonUrl) ? (
                    <a
                      href={fileReportsJsonUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.secondaryButton}
                      title="Temporary Stage 2 diagnostic artifact"
                    >
                      Open Stage 2 File Reports JSON
                    </a>
                  ) : null}

                  <Link to="/case-analyzer/history" style={styles.secondaryButton}>
                    Back to Analysis History
                  </Link>

                  <Link to="/file-vault" style={styles.secondaryButton}>
                    Go to File Vault
                  </Link>

                  <Link to="/case-analyzer" style={styles.ghostButton}>
                    New Analysis
                  </Link>
                </div>
              </section>

              {isCompact ? (
                <div className="case-dashboard-mobile-insights" style={styles.mobileInsights}>
                  <QuickViewPanel
                    probabilityScore={probabilityScore}
                    evidenceScore={evidenceScore}
                    reliabilityScore={reliabilityScore}
                    settlementScore={settlementScore}
                  />
                  <StrengthsPanel items={keyStrengths} />
                  <RisksPanel items={keyRisks} />
                </div>
              ) : null}

              <section style={styles.dashboardPanel}>
                <div className="case-dashboard-panel-header" style={styles.panelHeader}>
                  <div>
                    <h2 style={styles.sectionTitle}>Report visuals</h2>
                  </div>
                  <span className="case-dashboard-small-pill" style={styles.smallPill}>
                    {rendererText || detailedScoreItems.length ? "Available" : "Pending"}
                  </span>
                </div>

                <DashboardVisualCards
                  probabilityScore={probabilityScore}
                  probabilityBand={probabilityBand}
                  outcomeRangeItems={outcomeRangeItems}
                  detailedScoreItems={detailedScoreItems}
                />

                {rendererText ? (
                  <div className="case-dashboard-renderer-shell" style={styles.answerRendererShell}>
                    <div
                      className="case-dashboard-renderer-canvas"
                      style={styles.answerRendererCanvas}
                    >
                      <SafeAnswerRenderer text={rendererText} />
                    </div>
                  </div>
                ) : (
                  <div style={styles.emptyBox}>
                    Report chart, list, and summary data will appear when the dashboard artifact is available.
                  </div>
                )}
              </section>

              <section style={styles.evidencePanel}>
                <h2 style={styles.sectionTitle}>Evidence package</h2>
                <p style={styles.sectionText}>
                  Summary of the evidence reviewed for this dashboard.
                </p>

                <div style={styles.fileList}>
                  {files.length ? (
                    <>
                      {visibleEvidenceFiles.map((file, idx) => (
                        <div
                          className="case-dashboard-file-row"
                          key={file.fileId || file.file_id || file.name || idx}
                          style={styles.fileRow}
                        >
                          <div style={styles.fileTextBlock}>
                            <div className="case-dashboard-file-name" style={styles.fileName}>
                              {cleanDisplayText(
                                file.originalName ||
                                  file.original_name ||
                                  file.name ||
                                  file.file_name ||
                                  `Evidence file ${idx + 1}`
                              )}
                            </div>
                            <div className="case-dashboard-file-meta" style={styles.fileMeta}>
                              {cleanDisplayText(
                                file.evidenceBundleCategory ||
                                  file.evidence_bundle_category ||
                                  file.bundle ||
                                  file.selected_bundle_label ||
                                  file.resolved_bundle_label ||
                                  "Evidence"
                              )}{" "}
                              ·{" "}
                              {cleanDisplayText(
                                file.status || file.extraction_method || "reviewed"
                              )}
                            </div>
                          </div>
                          <span style={styles.fileBadge}>
                            {cleanDisplayText(
                              file.pageCount ||
                                file.page_count ||
                                file.page_count_status ||
                                "?"
                            )}{" "}
                            pages
                          </span>
                        </div>
                      ))}

                      {files.length > evidenceFilePreviewLimit ? (
                        <button
                          type="button"
                          onClick={() => setShowAllEvidenceFiles((prev) => !prev)}
                          style={styles.showMoreFilesButton}
                        >
                          {showAllEvidenceFiles
                            ? "Show fewer files"
                            : `Show ${hiddenEvidenceFileCount} more file${
                                hiddenEvidenceFileCount === 1 ? "" : "s"
                              }`}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <div style={styles.emptyBox}>
                      Evidence file details will appear when the dashboard artifact includes reviewed document data.
                    </div>
                  )}
                </div>
              </section>

              {isCompact ? <ImportantNotePanel /> : null}
            </main>

            {!isCompact ? (
              <aside style={styles.sideColumn}>
                <QuickViewPanel
                  probabilityScore={probabilityScore}
                  evidenceScore={evidenceScore}
                  reliabilityScore={reliabilityScore}
                  settlementScore={settlementScore}
                />
                <StrengthsPanel items={keyStrengths} />
                <RisksPanel items={keyRisks} />
                <ImportantNotePanel />
              </aside>
            ) : null}
          </div>
        </div>
      ) : null}

    </div>
  );
}



function QuickViewPanel({
  probabilityScore,
  evidenceScore,
  reliabilityScore,
  settlementScore,
}) {
  return (
    <section className="case-dashboard-side-panel" style={styles.sidePanel}>
      <h2 style={styles.sideTitle}>Quick view</h2>
      <div style={styles.quickMetric}>
        <span>Probability</span>
        <strong>{Math.round(probabilityScore)}%</strong>
      </div>
      <div style={styles.quickMetric}>
        <span>Evidence</span>
        <strong>{Math.round(evidenceScore)}%</strong>
      </div>
      <div style={styles.quickMetric}>
        <span>Reliability</span>
        <strong>{Math.round(reliabilityScore)}%</strong>
      </div>
      <div style={styles.quickMetric}>
        <span>Settlement</span>
        <strong>{Math.round(settlementScore)}%</strong>
      </div>
    </section>
  );
}

function StrengthsPanel({ items }) {
  return (
    <section className="case-dashboard-side-panel" style={styles.sidePanel}>
      <h2 style={styles.sideTitle}>Top strengths</h2>
      <SimpleList
        items={items}
        emptyText="Strength findings will appear when available."
        tone="good"
      />
    </section>
  );
}

function RisksPanel({ items }) {
  return (
    <section className="case-dashboard-side-panel" style={styles.sidePanel}>
      <h2 style={styles.sideTitle}>Top risks / concerns</h2>
      <SimpleList
        items={items}
        emptyText="Risk findings will appear when available."
        tone="risk"
      />
    </section>
  );
}

function ImportantNotePanel() {
  return (
    <section className="case-dashboard-important-note" style={styles.methodPanel}>
      <h2 style={styles.sideTitle}>Important note</h2>
      <p style={styles.sideCopy}>
        This dashboard is an evidence and risk analysis summary. It is not legal
        advice and should be reviewed with appropriate professional judgment.
      </p>
    </section>
  );
}

function DashboardVisualCards({
  probabilityScore,
  probabilityBand,
  outcomeRangeItems,
  detailedScoreItems,
}) {
  const hasAny = outcomeRangeItems.length || detailedScoreItems.length;

  if (!hasAny) {
    return (
      <div style={{ ...styles.emptyBox, marginTop: 14 }}>
        Dashboard visuals will appear when the completed dashboard artifact includes score, range, table, or chart data.
      </div>
    );
  }

  return (
    <div style={styles.visualStack}>
      <div className="case-dashboard-visual-grid" style={styles.visualGrid}>
        <OutcomeVisualCard
          probabilityScore={probabilityScore}
          probabilityBand={probabilityBand}
          outcomeRangeItems={outcomeRangeItems}
        />

        <HorizontalScoreBars
          title="Case Analyzer Score Dashboard"
          subtitle="Full dashboard scoring profile used by the report."
          items={detailedScoreItems}
        />
      </div>
    </div>
  );
}

function OutcomeVisualCard({ probabilityScore, probabilityBand, outcomeRangeItems }) {
  return (
    <div style={styles.visualCard}>
      <div style={styles.visualHeader}>
        <div>
          <h3 style={styles.visualTitle}>Probability / Outcome Profile</h3>
          <p style={styles.visualSubtitle}>Outcome posture and score range snapshot.</p>
        </div>
      </div>

      <div style={styles.outcomeVisualBody}>
        <div style={styles.outcomeScoreBlock}>
          <div style={styles.outcomeScore}>{Math.round(clamp(probabilityScore))}</div>
          <div style={styles.outcomeBand}>{cleanDisplayText(probabilityBand, "Pending")}</div>
          <div style={styles.outcomeScale}>/100</div>
        </div>

        {outcomeRangeItems.length ? (
          <div style={styles.outcomeRangeGrid}>
            {outcomeRangeItems.map((item) => (
              <div key={item.label} style={styles.outcomeRangeBox}>
                <div style={styles.outcomeRangeLabel}>{item.label}</div>
                <div style={styles.outcomeRangeValue}>{item.value}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const SCORE_DASHBOARD_BAR_COLORS = {
  evidencestrength: "#059669",
  evidencereliability: "#7C3AED",
  claimorpositionsupport: "#2563EB",
  claimpositionsupport: "#2563EB",
  othersiderisk: "#DC2626",
  damagessupport: "#059669",
  timelinestrength: "#059669",
  proceduraldeadlinerisk: "#DC2626",
  missingevidencerisk: "#DC2626",
  contradictionrisk: "#DC2626",
  settlementleverage: "#EA580C",
  scorereliability: "#7C3AED",
};

function getScoreDashboardBarColor(label) {
  return SCORE_DASHBOARD_BAR_COLORS[normalizeKey(label)] || "#2563EB";
}

function HorizontalScoreBars({ title, subtitle, items, showRating = false, compact = false }) {
  const rows = asArray(items).filter((item) => item && isFiniteNumberLike(item.value)).slice(0, compact ? 8 : 14);

  if (!rows.length) return null;

  return (
    <div style={styles.visualCard}>
      <div style={styles.visualHeader}>
        <div>
          <h3 style={styles.visualTitle}>{title}</h3>
          {subtitle ? <p style={styles.visualSubtitle}>{subtitle}</p> : null}
        </div>
      </div>

      <div
        className="case-dashboard-score-scroll"
        style={styles.scoreBarsScroll}
      >
        <div
          className="case-dashboard-score-canvas"
          style={styles.scoreBarsCanvas}
        >
          <div style={styles.barList}>
            {rows.map((item, idx) => {
              const pct = clamp(item.value);
              const barColor = getScoreDashboardBarColor(item.label);
              return (
                <div key={`${item.label}-${idx}`} style={styles.barRow}>
                  <div style={styles.barRowTop}>
                    <span style={styles.barLabel}>
                      {cleanDisplayText(item.label)}
                    </span>
                    <span style={styles.barValue}>
                      {Math.round(pct)}
                      {showRating && item.rating ? (
                        <span style={styles.barRating}> · {item.rating}</span>
                      ) : null}
                    </span>
                  </div>
                  <div style={styles.barTrack}>
                    <div style={{ ...styles.barFill, width: `${pct}%`, background: barColor }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function PriorityEvidenceCard({ items }) {
  const rows = asArray(items).filter(Boolean).slice(0, 8);
  if (!rows.length) return null;

  return (
    <div style={styles.visualCard}>
      <div style={styles.visualHeader}>
        <div>
          <h3 style={styles.visualTitle}>Missing Evidence Priority</h3>
          <p style={styles.visualSubtitle}>Priority evidence items reflected in the dashboard.</p>
        </div>
      </div>

      <div style={styles.priorityList}>
        {rows.map((item, idx) => {
          const text = cleanDisplayText(
            item?.missing_item || item?.missingItem || item?.item || item?.question || item?.title || item?.text || normalizeTextItem(item),
            `Evidence item ${idx + 1}`
          );
          const source = cleanDisplayText(item?.source || item?.source_reference || item?.document, "");
          const priority = cleanDisplayText(item?.priority || item?.severity || item?.confidence, "");

          return (
            <div key={`${text}-${idx}`} style={styles.priorityRow}>
              <div style={styles.priorityText}>{text}</div>
              {source || priority ? (
                <div style={styles.priorityMeta}>
                  {source ? <span><strong>Source:</strong> {source}</span> : null}
                  {source && priority ? <span> · </span> : null}
                  {priority ? <span><strong>Priority:</strong> {priority}</span> : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function SafeAnswerRenderer({ text }) {
  try {
    return <AnswerRenderer text={text || ""} isStreaming={false} />;
  } catch (err) {
    return (
      <div style={styles.emptyBox}>
        Report visuals could not be displayed. The dashboard summary and score cards are still available.
      </div>
    );
  }
}

function ScoreTile({ title, value, label, color = "#2563EB", variant = "ring" }) {
  const pct = clamp(value);
  return (
    <div style={styles.scoreTile}>
      <div style={styles.scoreTitle}>{title}</div>
      {variant === "bar" ? (
        <div style={styles.scoreBarWrap}>
          <div style={styles.scoreBarTop}>
            <span style={{ ...styles.scoreBig, color }}>{Math.round(pct)}</span>
            <span style={styles.scoreUnit}>/100</span>
          </div>
          <div style={styles.scoreTrack}>
            <div style={{ ...styles.scoreFill, background: color, width: `${pct}%` }} />
          </div>
        </div>
      ) : (
        <div style={styles.gaugeWrap}>
          <div
            style={{
              ...styles.gauge,
              background: `conic-gradient(${color} 0deg, ${color} ${pct * 3.6}deg, #E2E8F0 ${pct * 3.6}deg, #E2E8F0 360deg)`,
            }}
          >
            <div style={styles.gaugeInner}>
              <div style={{ ...styles.gaugeValue, color }}>{Math.round(pct)}</div>
              <div style={styles.gaugeUnit}>/100</div>
            </div>
          </div>
        </div>
      )}
      <div style={styles.gaugeLabel}>{label}</div>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{cleanDisplayText(value)}</div>
    </div>
  );
}

function CaseTypeFrameworkPanel({ framework }) {
  const data = framework || {};
  const routerUsed = Boolean(data.caseTypeRouterUsed || data.case_type_router_used || data.routerUsed || data.router_used);
  const rows = [
    ["Selected case type", data.selectedCaseTypeLabel || data.selected_case_type_label || data.selectedCaseTypeId || data.selected_case_type_id],
    ["Framework used", data.frameworkUsedLabel || data.framework_used_label || data.caseTypeLabel || data.case_type_label || data.frameworkUsedId || data.framework_used_id],
    ["Selection mode", data.selectionMode || data.selection_mode],
    ["Case playbook path", data.caseTypePlaybookPath || data.case_type_playbook_path],
    ["Universal framework path", data.universalFrameworkPath || data.universal_report_framework_path],
    ["Case router used", routerUsed],
    ["Router confidence", data.routerConfidence || data.router_confidence || data.classificationConfidence || data.classification_confidence],
    ["Router reason", data.routerReason || data.router_reason || data.classificationReason || data.classification_reason],
  ].filter(([, value]) => cleanDisplayText(value, ""));

  if (!rows.length) {
    return <div style={styles.emptyBox}>Case-level playbook diagnostics are not available yet.</div>;
  }

  return (
    <div style={styles.diagnosticCard}>
      <h3 style={styles.diagnosticTitle}>Case-level report playbook</h3>
      <div style={styles.kvGrid}>
        {rows.map(([label, value]) => (
          <div key={label} style={styles.kvItem}>
            <div style={styles.kvLabel}>{label}</div>
            <div style={styles.kvValue}>{cleanDisplayText(value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EvidencePlaybookPanel({ rows }) {
  const safeRows = asArray(rows).filter(Boolean);
  if (!safeRows.length) {
    return <div style={styles.emptyBox}>Evidence playbook diagnostics are not available yet.</div>;
  }

  return (
    <div style={styles.diagnosticCard}>
      <h3 style={styles.diagnosticTitle}>Evidence-bundle playbooks used</h3>
      <div style={styles.auditTableWrap}>
        <table style={styles.auditTable}>
          <thead>
            <tr>
              <th style={styles.auditTh}>File</th>
              <th style={styles.auditTh}>Selected</th>
              <th style={styles.auditTh}>Resolved</th>
              <th style={styles.auditTh}>Playbook path</th>
              <th style={styles.auditTh}>Router</th>
              <th style={styles.auditTh}>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {safeRows.map((row) => (
              <tr key={row.key}>
                <td style={styles.auditTd}>{cleanDisplayText(row.fileName)}</td>
                <td style={styles.auditTd}>{cleanDisplayText(row.selected)}</td>
                <td style={styles.auditTd}>{cleanDisplayText(row.resolved)}</td>
                <td style={styles.auditTd}>{cleanDisplayText(row.playbookPath, "—")}</td>
                <td style={styles.auditTd}>{row.routerUsed ? "Yes" : "No"}</td>
                <td style={styles.auditTd}>{cleanDisplayText(row.confidence, "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CodePath({ value }) {
  return <div style={styles.codePath}>{cleanDisplayText(value)}</div>;
}

function SimpleList({ items, emptyText, tone }) {
  const icon = tone === "risk" ? "△" : "✓";
  const color = tone === "risk" ? "#EF4444" : "#059669";

  if (!items.length) {
    return <div style={styles.emptyMini}>{emptyText}</div>;
  }

  return (
    <div style={styles.simpleList}>
      {items.map((item, idx) => (
        <div key={`${item.text}-${idx}`} style={styles.simpleListRow}>
          <span style={{ ...styles.simpleListIcon, color }}>{icon}</span>
          <div>
            <div style={styles.simpleListText}>{item.text}</div>
            {item.reason ? <div style={styles.simpleListMeta}>{item.reason}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function Spinner({ size = 18, color = "#2563EB" }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "999px",
        border: `${Math.max(2, Math.round(size / 8))}px solid ${color}33`,
        borderTopColor: color,
        display: "inline-block",
        animation: "caseAnalyzerSpin .75s linear infinite",
        flex: "0 0 auto",
      }}
    />
  );
}

const styles = {
  outerWrap: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "18px 16px 42px",
    boxSizing: "border-box",
    background: "#F5F7FA",
    fontFamily: "sans-serif",
    minHeight: "100vh",
    width: "100%",
    overflowX: "hidden",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
    marginBottom: 16,
    minWidth: 0,
    maxWidth: "100%",
  },
  kicker: {
    color: "#2563EB",
    fontSize: 13,
    fontWeight: 950,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 7,
  },
  title: {
    margin: 0,
    color: "#0F172A",
    fontSize: 30,
    lineHeight: 1.08,
    fontWeight: 950,
    letterSpacing: "-1px",
  },
  subtitle: {
    margin: "9px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    maxWidth: 760,
    fontWeight: 650,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "10px 13px",
    borderRadius: 999,
    border: "1px solid rgba(37,99,235,0.18)",
    boxShadow: "0 12px 24px rgba(15,23,42,0.06)",
    fontWeight: 950,
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  statusDot: { width: 10, height: 10, borderRadius: 999 },
  loadingCard: {
    marginTop: 80,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    padding: 22,
    borderRadius: 20,
    background: "#FFFFFF",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
  },
  loadingTitle: { color: "#0F172A", fontWeight: 950 },
  errorBox: {
    marginBottom: 14,
    padding: 13,
    borderRadius: 16,
    background: "#FEF2F2",
    border: "1px solid rgba(220,38,38,0.20)",
    color: "#991B1B",
    fontWeight: 750,
  },
  progressPanel: {
    borderRadius: 22,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#FFFFFF",
    boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
    padding: 16,
    marginBottom: 16,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },
  progressTop: { display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" },
  progressCopy: { minWidth: 0 },
  stageEyebrow: { fontWeight: 950, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 },
  progressTitle: { margin: 0, color: "#0F172A", fontSize: 24, fontWeight: 950, letterSpacing: "-0.5px" },
  sectionTitle: { margin: 0, color: "#0F172A", fontSize: 20, fontWeight: 950, letterSpacing: "-0.3px" },
  sectionText: { margin: "5px 0 0", color: "#64748B", fontSize: 13, lineHeight: 1.45, fontWeight: 650 },
  processingNote: {
    marginTop: 14,
    padding: "12px 13px",
    borderRadius: 16,
    border: "1px solid rgba(37,99,235,0.16)",
    background: "linear-gradient(180deg, #F8FBFF 0%, #EFF6FF 100%)",
    color: "#1E3A5F",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },
  processingNoteHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  processingNoteLead: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    minWidth: 0,
  },
  processingNoteIcon: {
    width: 24,
    height: 24,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
    background: "#DBEAFE",
    color: "#2563EB",
    fontSize: 12,
    fontWeight: 950,
  },
  processingNoteTitle: {
    color: "#1E3A5F",
    fontSize: 13,
    lineHeight: 1.4,
    overflowWrap: "anywhere",
  },
  processingNoteToggle: {
    border: "none",
    background: "transparent",
    color: "#2563EB",
    padding: "4px 0",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  processingNoteText: {
    margin: "8px 0 0 33px",
    color: "#475569",
    fontSize: 12.5,
    lineHeight: 1.55,
    fontWeight: 650,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  jobBadge: {
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    padding: "8px 10px",
    borderRadius: 999,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    color: "#64748B",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  instrumentPanel: {
    marginTop: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "linear-gradient(180deg,#FFFFFF,#F8FAFC)",
    padding: 13,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },
  instrumentTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  instrumentLabel: { color: "#0F172A", fontWeight: 950, fontSize: 14 },
  instrumentSub: { color: "#64748B", fontWeight: 750, fontSize: 12, marginTop: 3 },
  instrumentPercent: { fontWeight: 950, fontSize: 30, lineHeight: 1 },
  segmentTrack: { height: 12, borderRadius: 999, background: "#E2E8F0", overflow: "hidden", marginTop: 12 },
  segmentFill: { height: "100%", borderRadius: 999, transition: "width .35s ease" },
  segmentLabels: { display: "flex", justifyContent: "space-between", gap: 8, color: "#64748B", fontWeight: 800, fontSize: 11, marginTop: 7 },
  stageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(9, minmax(0, 1fr))",
    gap: 9,
    marginTop: 14,
    minWidth: 0,
    maxWidth: "100%",
  },
  stageCard: {
    padding: 10,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#F8FAFC",
    minHeight: 82,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    transition: "box-shadow .2s ease, transform .2s ease",
  },
  stageDone: { background: "#FFFFFF" },
  stageCurrent: { background: "#FFFFFF", boxShadow: "0 0 0 4px rgba(37,99,235,0.10)", transform: "translateY(-1px)" },
  stagePending: { opacity: 0.72 },
  stageTop: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
  stageIcon: {
    width: 26,
    height: 26,
    borderRadius: 11,
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
    fontSize: 12,
  },
  stageLabel: {
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 12,
    lineHeight: 1.25,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  stageText: { color: "#64748B", fontWeight: 650, lineHeight: 1.35, fontSize: 11, marginTop: 4 },
  mainGrid: {
    maxWidth: 1500,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 360px",
    gap: 22,
    alignItems: "start",
  },
  mainColumn: { minWidth: 0 },
  sideColumn: { display: "grid", gap: 14 },
  scoreGrid: {
    maxWidth: 1500,
    margin: "0 auto 18px",
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
  },
  scoreTile: {
    minWidth: 0,
    borderRadius: 22,
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.22)",
    boxShadow: "0 18px 42px rgba(15,23,42,0.06)",
    padding: 18,
    textAlign: "center",
    overflow: "hidden",
  },
  scoreTitle: { color: "#0F172A", fontWeight: 950, fontSize: 14, marginBottom: 10 },
  gaugeWrap: { display: "grid", placeItems: "center" },
  gauge: {
    width: 112,
    height: 112,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
  },
  gaugeInner: {
    width: 78,
    height: 78,
    borderRadius: "50%",
    background: "#FFFFFF",
    display: "grid",
    placeItems: "center",
    boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.06)",
  },
  gaugeValue: { fontWeight: 950, fontSize: 25, lineHeight: 1 },
  gaugeUnit: { color: "#64748B", fontWeight: 800, fontSize: 11 },
  gaugeLabel: { color: "#64748B", fontWeight: 700, fontSize: 12, marginTop: 8 },
  scoreBarWrap: { minHeight: 112, display: "grid", alignContent: "center", gap: 10 },
  scoreBarTop: { display: "flex", justifyContent: "center", alignItems: "baseline", gap: 3 },
  scoreBig: { fontWeight: 950, fontSize: 36, lineHeight: 1 },
  scoreUnit: { color: "#64748B", fontWeight: 800, fontSize: 12 },
  scoreTrack: { height: 12, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" },
  scoreFill: { height: "100%", borderRadius: 999 },
  reportPanel: {
    minWidth: 0,
    borderRadius: 22,
    border: "1px solid rgba(148,163,184,0.22)",
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 20px 46px rgba(15,23,42,0.07)",
    padding: 20,
    marginBottom: 18,
    overflow: "hidden",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    minWidth: 0,
  },
  smallPill: {
    minWidth: 0,
    maxWidth: 420,
    padding: "7px 10px",
    borderRadius: 999,
    background: "#F1F5F9",
    color: "#334155",
    fontWeight: 900,
    fontSize: 12,
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
    lineHeight: 1.35,
  },
  summaryText: {
    margin: "15px 0",
    color: "#334155",
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 16,
    padding: 14,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  reportGrid: { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 },
  metricCard: {
    borderRadius: 16,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    padding: 12,
  },
  metricLabel: { color: "#64748B", fontWeight: 850, fontSize: 12 },
  metricValue: { color: "#0F172A", fontWeight: 950, fontSize: 15, marginTop: 5, wordBreak: "break-word" },
  actionRow: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 },
  primaryButton: {
    border: "none",
    borderRadius: 14,
    padding: "12px 14px",
    background: "#2563EB",
    color: "#FFFFFF",
    fontWeight: 950,
    textDecoration: "none",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  },
  secondaryButton: {
    borderRadius: 14,
    padding: "12px 14px",
    background: "#FFFFFF",
    color: "#1D4ED8",
    fontWeight: 950,
    textDecoration: "none",
    border: "1px solid rgba(37,99,235,0.18)",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
  },
  ghostButton: {
    borderRadius: 14,
    padding: "12px 14px",
    background: "#F8FAFC",
    color: "#334155",
    fontWeight: 950,
    textDecoration: "none",
    border: "1px solid rgba(15,23,42,0.08)",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
  },
  disabledButton: { opacity: 0.55, cursor: "not-allowed" },
  evidencePanel: {
    minWidth: 0,
    borderRadius: 22,
    border: "1px solid rgba(148,163,184,0.22)",
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 20px 46px rgba(15,23,42,0.07)",
    padding: 20,
    overflow: "hidden",
  },
  evidenceStatsRow: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginTop: 12 },
  fileList: { display: "grid", gap: 8, marginTop: 12 },
  fileRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    padding: 12,
    borderRadius: 14,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
  },
  fileName: { color: "#0F172A", fontWeight: 900, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fileMeta: { color: "#64748B", fontWeight: 700, fontSize: 12, marginTop: 3, overflowWrap: "anywhere" },
  fileBadge: {
    padding: "6px 8px",
    borderRadius: 999,
    background: "#EFF6FF",
    color: "#1E3A8A",
    fontWeight: 950,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  diagnosticCard: {
    marginTop: 14,
    padding: 13,
    borderRadius: 16,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
  },
  diagnosticTitle: {
    margin: "0 0 10px",
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
  },
  kvGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 9,
  },
  kvItem: {
    padding: 10,
    borderRadius: 12,
    background: "#FFFFFF",
    border: "1px solid rgba(15,23,42,0.07)",
  },
  kvLabel: {
    color: "#64748B",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  kvValue: {
    marginTop: 4,
    color: "#0F172A",
    fontSize: 12.5,
    fontWeight: 800,
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  auditTableWrap: {
    overflowX: "auto",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#FFFFFF",
  },
  auditTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 760,
    fontSize: 12,
  },
  auditTh: {
    textAlign: "left",
    padding: "9px 10px",
    background: "#EFF6FF",
    color: "#1E3A8A",
    fontWeight: 950,
    borderBottom: "1px solid rgba(15,23,42,0.08)",
  },
  auditTd: {
    padding: "9px 10px",
    color: "#334155",
    fontWeight: 750,
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    verticalAlign: "top",
    wordBreak: "break-word",
  },
  emptyBox: {
    padding: 13,
    borderRadius: 14,
    background: "#F8FAFC",
    border: "1px dashed rgba(15,23,42,0.18)",
    color: "#64748B",
    fontWeight: 700,
  },
  sidePanel: {
    borderRadius: 22,
    border: "1px solid rgba(148,163,184,0.22)",
    background: "rgba(255,255,255,0.96)",
    boxShadow: "0 20px 46px rgba(15,23,42,0.07)",
    padding: 18,
  },
  sideTitle: { color: "#0F172A", fontWeight: 950, fontSize: 16, margin: "0 0 12px" },
  sideCopy: { margin: 0, color: "#64748B", fontWeight: 700, fontSize: 13, lineHeight: 1.5 },
  codePath: {
    marginTop: 10,
    borderRadius: 12,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    padding: 10,
    color: "#334155",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  dashboardPanel: {
    minWidth: 0,
    borderRadius: 22,
    border: "1px solid rgba(148,163,184,0.22)",
    background: "rgba(255,255,255,0.94)",
    boxShadow: "0 20px 46px rgba(15,23,42,0.07)",
    padding: 20,
    marginBottom: 18,
    overflow: "hidden",
  },
  answerRendererShell: {
    marginTop: 14,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    borderRadius: 18,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    padding: 12,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
  },
  pendingHistoryBox: {
    margin: "10px 0",
    padding: 12,
    borderRadius: 14,
    background: "#FFFBEB",
    border: "1px solid rgba(245,158,11,0.25)",
    color: "#92400E",
    fontWeight: 750,
    fontSize: 13,
  },
  artifactPathBox: {
    margin: "10px 0 12px",
    padding: 12,
    borderRadius: 14,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    color: "#475569",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.6,
    wordBreak: "break-word",
  },

  twoColumnGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    marginTop: 14,
  },
  visualStack: {
    display: "grid",
    gap: 14,
    marginTop: 14,
  },
  visualGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
    gap: 14,
  },
  visualCard: {
    minWidth: 0,
    maxWidth: "100%",
    borderRadius: 18,
    background: "#FFFFFF",
    border: "1px solid rgba(148,163,184,0.22)",
    boxShadow: "0 14px 34px rgba(15,23,42,0.05)",
    padding: 16,
    overflow: "hidden",
  },
  visualHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 12,
  },
  visualTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
    letterSpacing: "-0.2px",
  },
  visualSubtitle: {
    margin: "4px 0 0",
    color: "#64748B",
    fontSize: 12,
    fontWeight: 750,
    lineHeight: 1.45,
  },
  outcomeVisualBody: {
    display: "grid",
    gridTemplateColumns: "minmax(130px, 160px) 1fr",
    gap: 14,
    alignItems: "center",
  },
  outcomeScoreBlock: {
    minHeight: 126,
    borderRadius: 18,
    background: "linear-gradient(135deg, #EFF6FF, #FFFFFF)",
    border: "1px solid rgba(37,99,235,0.16)",
    display: "grid",
    placeItems: "center",
    padding: 12,
    textAlign: "center",
  },
  outcomeScore: {
    color: "#2563EB",
    fontSize: 42,
    lineHeight: 1,
    fontWeight: 950,
  },
  outcomeBand: {
    marginTop: 5,
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 14,
  },
  outcomeScale: {
    marginTop: 2,
    color: "#64748B",
    fontWeight: 850,
    fontSize: 12,
  },
  barList: {
    display: "grid",
    gap: 10,
  },
  barRow: {
    display: "grid",
    gap: 6,
  },
  barRowTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 12,
  },
  barLabel: {
    minWidth: 0,
    color: "#334155",
    fontWeight: 900,
    fontSize: 13,
    lineHeight: 1.25,
  },
  barValue: {
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  barRating: {
    color: "#64748B",
    fontWeight: 800,
  },
  barTrack: {
    height: 10,
    borderRadius: 999,
    background: "#E2E8F0",
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #2563EB, #1D4ED8)",
  },
  priorityList: {
    display: "grid",
    gap: 10,
  },
  priorityRow: {
    borderRadius: 14,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    padding: 11,
  },
  priorityText: {
    color: "#0F172A",
    fontWeight: 900,
    fontSize: 13,
    lineHeight: 1.35,
  },
  priorityReason: {
    color: "#475569",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 4,
  },
  priorityMeta: {
    marginTop: 5,
    color: "#64748B",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.4,
  },
  findingList: {
    display: "grid",
    gap: 10,
    marginTop: 10,
  },
  findingRow: {
    display: "grid",
    gridTemplateColumns: "20px 1fr",
    gap: 8,
    alignItems: "start",
  },
  findingIcon: {
    fontWeight: 950,
    fontSize: 13,
    lineHeight: 1.4,
  },
  findingText: {
    color: "#0F172A",
    fontWeight: 850,
    fontSize: 13,
    lineHeight: 1.4,
  },
  findingReason: {
    color: "#475569",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.4,
    marginTop: 3,
  },
  findingMeta: {
    color: "#64748B",
    fontWeight: 750,
    fontSize: 11,
    marginTop: 3,
  },


  completedDashboardWrap: {
    marginTop: 22,
  },

  answerRendererCanvas: {
    display: "block",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    background: "#F8FAFC",
    borderRadius: 14,
    verticalAlign: "top",
    overflow: "visible",
  },

  mobileInsights: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 14,
    marginBottom: 18,
  },

  visualColumn: {
    display: "grid",
    gap: 12,
    alignContent: "start",
    minWidth: 0,
  },

  outcomeRangeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
  },

  outcomeRangeBox: {
    borderRadius: 14,
    background: "#F8FAFC",
    border: "1px solid rgba(15,23,42,0.08)",
    padding: 11,
  },

  outcomeRangeLabel: {
    color: "#64748B",
    fontWeight: 950,
    textTransform: "uppercase",
    fontSize: 10,
    letterSpacing: ".04em",
    lineHeight: 1.25,
  },

  outcomeRangeValue: {
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 17,
    marginTop: 7,
  },

  showMoreFilesButton: {
    marginTop: 12,
    width: "100%",
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#1E2A78",
    borderRadius: 14,
    padding: "11px 14px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  fileTextBlock: { minWidth: 0, overflow: "hidden" },

  quickMetric: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid rgba(148,163,184,0.18)",
    color: "#475569",
    fontWeight: 850,
  },

  simpleList: { display: "grid", gap: 10 },

  simpleListRow: {
    display: "grid",
    gridTemplateColumns: "18px 1fr",
    gap: 8,
    alignItems: "start",
  },

  simpleListIcon: {
    fontWeight: 950,
    marginTop: 1,
  },

  simpleListText: {
    color: "#334155",
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.4,
  },

  simpleListMeta: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 650,
    marginTop: 3,
    lineHeight: 1.35,
  },

  methodPanel: {
    borderRadius: 22,
    border: "1px solid rgba(245,158,11,0.25)",
    background: "#FFFBEB",
    boxShadow: "0 14px 34px rgba(15,23,42,0.05)",
    padding: 18,
  },

  emptyMini: {
    color: "#64748B",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.5,
  },
  scoreBarsScroll: {
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
  },
  scoreBarsCanvas: {
    display: "inline-block",
    width: "max-content",
    minWidth: "100%",
    maxWidth: "none",
    background: "#FFFFFF",
    borderRadius: 12,
    verticalAlign: "top",
  },
};
